/**
 * AlphaX Tuning Simulator | Copyright (C) 2026 AlphaX
 * Licensed under GPL-3.0 (https://www.gnu.org/licenses/gpl-3.0.txt)
 * Subscriptions do not negate this license; derivative works must be open source.
 */
/**
 * AlphaX Tuning Simulator - Main Loop & Initialization
 */

(function() {
    const allowed = [
        'alphaxpr.com',
        'tunersim.com',
        'tuningsim.com',
        'tunersimulator.com',
        'tuning-sim.firebaseapp.com',
        'tuning-sim.web.app',
        'localhost',
        '127.0.0.1'
    ];
    const host = window.location.hostname.toLowerCase();
    const isAllowed = host === '' || allowed.some(domain => host === domain || host.endsWith('.' + domain));
    if (!isAllowed) {
        console.error("AlphaX License Violation: This domain is not authorized to host this simulator.");
        document.addEventListener('DOMContentLoaded', () => {
            document.body.innerHTML = `
                <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;background:#000;color:#fff;font-family:sans-serif;text-align:center;padding:20px;">
                    <h1 style="color:#ff4d4d;font-size:3rem;margin-bottom:1rem;">UNAUTHORIZED COPY</h1>
                    <p style="font-size:1.2rem;max-width:600px;line-height:1.6;">
                        This version of the AlphaX Tuning Simulator is being hosted illegally. 
                        Please visit the official site at <strong><a href="https://alphaxpr.com" style="color:#4da6ff;text-decoration:none;">alphaxpr.com</a></strong> for the authentic, up-to-date experience.
                    </p>
                    <div style="margin-top:2rem;font-size:0.8rem;color:#666;">Error Code: AX-LIC-403</div>
                </div>
            `;
        });
        throw new Error("AlphaX License Violation");
    }
})();

let isEngineRunning = false;
let currentRpm = 0;
let nitrousActive = false;     // true when N2O solenoid is open this frame
let nitrousSwitchHeld = false; // tracks button hold state (switch/button mode)
let lastFrameAfr = 14.7;      // previous frame's AFR for ECU mode safety cut
let actualTps = 0;
let lastTpsForAudio = 0;
let prevActualTps = 0;  // for per-frame dTps calculation (accel enrichment)
let isPedalHeldDown = false;
let holdModeActive = false;
let lastTime = performance.now();
let engineAngle = 0;
let engineTimer = 0;
let idleIntegral = 0;
let prevIdleError = 0;
let firingOrderArr = [1, 3, 4, 2];
let coilMeltAccumulator = 0;
let dynoMode = 'dynojet'; // 'load' | 'rpm-lock' | 'dynojet'
let dynoRpmRate = 500;    // RPM/s for load dyno mode
let dynoLockRpm = 3000;   // target RPM for rpm-lock mode
let dynoGear = 4;         // selected gear for dyno pull (1-10)
let batteryVoltage = 12.5;
let altDuty = 80;
let altIntegral = 0;
let prevAltError = 0;
let currentIatC = 27;    // current IAT in °C, updated each frame for the IAT VE curve tracer
let currentCoolantC = null;    // engine coolant temp in °C; null = uninitialized (set on first frame)
let thermostatAngle = null;    // sine wave phase (radians); null = not yet in thermostat cycle
let coolantSimEnabled = false; // set from sim settings checkbox
let afrHistory = [];           // O2 transport delay ring buffer: [{t: ms, afr: value}, ...]
let tempUnitF = true;          // true = display °F, false = display °C
let useMetric = false;         // true = °C, kPa, KPH, Lambda default; false = °F, PSI, MPH, AFR default
let mapDisplayGauge = false;   // true = show MAP as gauge pressure (subtract baro); false = absolute

// --- Knock Control ---
let knockRetardDeg   = 0;
let knockSensorCount = 0;   // knock events detected by sensor (threshold-gated)
let spectrogramData  = [];  // array of { noise: dB, threshold: dB }
let isSpectrogramPaused = false;

// --- Knock Frequency Calibration ---
const KNOCK_FREQ_BINS = 128;   // 0–20 kHz divided into 128 bins (~156 Hz per bin)
const KNOCK_FREQ_MAX  = 20.0;  // kHz
let knockWaterfallColumns = []; // ring buffer of Float32Array[KNOCK_FREQ_BINS], max 300 columns
let knockFreqCanvasCtx    = null; // cached 2D context for the waterfall canvas

// --- Calibration Timing Adder ---
let calTimingAdderDeg       = 0;  // extra advance degrees injected during cal mode test
let calTimingAdderRemainSec = 0;  // countdown in seconds; 0 = inactive

// --- Knock Overlay (Normal Mode indicator) ---
let knockOverlayCount = 0;  // session total knock events (for overlay display)
let knockOverlayTimer = 0;  // seconds since last knock event; overlay hides after 3s

// --- Starter Mode ---
let starterMode = 'push';      // 'push' | 'hold'
let isStarterHeld = false;     // true while button is held down (hold mode only)
let prevWasCranking = false;   // tracks last frame's crank state for audio edge detection
let _holdStartClickConsumed = false; // swallows the click that fires after a hold-mode mousedown

// --- Cranking AFR Penalty System ---
let floodLevel = 0;              // 0.0-1.0, persists across stop/start until WOT-cleared
let crankAfrSampled = false;     // guard: evaluate only once, at end of 2-s base window
let crankAfrAccum = 0;           // running Σ(AFR × dt) — for time-weighted average
let crankAfrDt = 0;              // running Σ(dt) during cranking
let crankPenaltyApplied = false; // guard: apply post-start penalty only once per start
let crankingAfrObserved = 14.7; // time-weighted average AFR over the 2-s crank window
let crankTimeBonus = 0;          // extra seconds added to the 2-s base crank
let simpleGracePeriod = 0;       // non-advanced mode: seconds remaining in 5-s post-start grace
let isFloodClear = false;        // true when WOT flood-clear is active this frame
let postStartPenalty = 0;        // current torque penalty fraction (0-0.2)
let postStartPenaltyInit = 0;    // penalty magnitude at moment of start (for lerp)
let postStartPenaltyDur = 0;     // total fade duration in seconds
let postStartPenaltyTimer = 0;   // elapsed seconds since engine fired
let postCrankEnrichActive  = false; // true while post-crank enrichment is decaying
let postCrankEnrichInit    = 1.0;   // multiplier at moment cranking ends (from curve)
let postCrankEnrichCurrent = 1.0;   // current multiplier this frame
let postCrankCyclesAccum   = 0.0;   // accumulated engine cycles since crank end
let postCrankArmed         = false; // guard: arm enrichment only once per start

function displayTemp(degC) {
    return tempUnitF
        ? `${((degC * 9 / 5) + 32).toFixed(1)}°F`
        : `${degC.toFixed(1)}°C`;
}

function getAmbientC() {
    const el = document.getElementById('sim-ambient-temp');
    if (!el) return 27;
    const val = parseFloat(el.value) || (tempUnitF ? 80 : 27);
    return tempUnitF ? (val - 32) * 5 / 9 : val;
}

function displayPressureKpa(kpa) {
    return useMetric ? `${kpa.toFixed(1)} kPa` : `${(kpa * 0.14504).toFixed(1)} PSI`;
}
function displayPressurePsi(psi) {
    return useMetric ? `${(psi * 6.89476).toFixed(1)} kPa` : `${psi.toFixed(1)} psi`;
}
function displaySpeed(mps) {
    return useMetric ? `${(mps * 3.6).toFixed(1)} kph` : `${(mps * 2.237).toFixed(1)} mph`;
}

const THERM_OMEGA = (2 * Math.PI) / 40; // 40-second thermostat cycle period

function updateCoolantTemp(rpm, dt) {
    if (thermostatAngle !== null) {
        // Thermostat phase: smooth sine wave between 85–90°C
        thermostatAngle += THERM_OMEGA * dt;
        currentCoolantC = 87.5 + 2.5 * Math.sin(thermostatAngle);
        return;
    }

    // Linear warmup phase
    const idealIdleRpm = (typeof sampleEngine !== 'undefined' && sampleEngine?.global_specs?.ideal_idle_rpm) || 800;
    const rpmAboveIdle = rpm - idealIdleRpm;
    let rate = 0;
    if (rpm >= idealIdleRpm - 50) {
        const t = Math.max(0, Math.min(1, rpmAboveIdle / 500));
        rate = 0.25 + t * (2.0 - 0.25);
    }
    currentCoolantC = Math.min(90, currentCoolantC + rate * dt);
    if (currentCoolantC >= 90) {
        // Transition to sine wave at the peak (Math.PI/2 = sin=1.0 → 87.5+2.5=90°C)
        thermostatAngle = Math.PI / 2;
    }
}

const elPower = document.getElementById('btn-power');
const elPedal = document.getElementById('btn-pedal');
const elSliderTps = document.getElementById('slider-tps');

function pressPedal() {
    if(holdModeActive) { 
        isPedalHeldDown = !isPedalHeldDown; 
        if (elPedal) elPedal.classList.toggle('pedal-active', isPedalHeldDown); 
    } else { 
        isPedalHeldDown = true; 
        if (elPedal) elPedal.classList.add('pedal-active'); 
    }
}

function releasePedal() {
    if(!holdModeActive) { 
        isPedalHeldDown = false; 
        if (elPedal) elPedal.classList.remove('pedal-active'); 
    }
}

// Event Listeners for Pedal
if (elPedal) {
    elPedal.addEventListener('mousedown', pressPedal);
    elPedal.addEventListener('mouseup', releasePedal);
    elPedal.addEventListener('mouseleave', releasePedal);
}

const toggleHold = document.getElementById('toggle-hold');
if (toggleHold) {
    toggleHold.addEventListener('change', (e) => holdModeActive = e.target.checked);
}

if (elSliderTps) {
    elSliderTps.addEventListener('input', (e) => {
        const lbl = document.getElementById('lbl-max-tps');
        if (lbl) lbl.innerText = `${e.target.value}%`;
    });
}

function resetEngineState(resetCoolant = true) {
    isEngineRunning = false;
    physics.engineBlown = false;
    physics.stallTimer = 0;
    physics.overrunTimer = 0;
    physics.knockCount = 0;
    physics.lastTorque = 0;
    postCrankEnrichActive = false; postCrankEnrichInit = 1.0;
    postCrankEnrichCurrent = 1.0; postCrankCyclesAccum = 0.0; postCrankArmed = false;
    physics.stft = 0;
    physics._prevActualAfr = 14.7;
    physics.afrNoiseCurrent = 0; physics.afrNoiseTarget = 0;
    afrHistory = [];
    physics.turboSpeedRpm = 0; // hard reset clears turbo spin-down state
    physics.scBoostKpa = 0;
    physics.actualVvtIntake  = 0; physics.vvtIntIn = 0; physics.vvtErrIn = 0;
    physics.actualVvtExhaust = 0; physics.vvtIntEx = 0; physics.vvtErrEx = 0;
    physics.effectiveWgDuty = 0; physics.clBoostIntegral = 0; physics.clBoostPrevErr = 0;
    physics.filteredMapKpa = -1; physics.accelCurrentMs = 0; physics.accelBlend = 0; physics.accelTimer = 0;
    currentRpm = 0;
    actualTps = 0;
    prevActualTps = 0;
    ecu.disabledCylinders = new Set();
    ecu.meltedCoils       = new Set();
    coilMeltAccumulator   = 0;
    knockRetardDeg   = 0;
    knockSensorCount = 0;
    spectrogramData  = [];
    knockOverlayCount = 0;
    knockOverlayTimer = 0;
    const _koEl = document.getElementById('knock-overlay');
    if (_koEl) { _koEl.classList.add('hidden'); }
    const _kcEl = document.getElementById('knock-overlay-count');
    if (_kcEl) { _kcEl.innerText = '0 events'; }
    if (resetCoolant) { currentCoolantC = null; thermostatAngle = null; } // re-initializes to ambient on next frame
    if (typeof updateMeltedCylinderDisplay === 'function') updateMeltedCylinderDisplay();
    if (typeof abortDynoPull === 'function') abortDynoPull();
    if (typeof abortDragRun === 'function') abortDragRun();
    if (audio) audio.stop();
    const errBox = document.getElementById('error-box');
    if (errBox) errBox.classList.add('hidden');
    const statusEl = document.getElementById('data-status');
    if (statusEl) { statusEl.innerText = "OFF"; statusEl.className = "text-red-400"; }
    elPower.innerText = "START ENGINE";
    elPower.className = "px-6 py-2 rounded font-bold bg-green-600 hover:bg-green-500 text-white shadow-lg shadow-green-900/50 transition-colors";
}

function _doEngineStart() {
    engineTimer = 0;
    physics.stallTimer = 0;
    physics.knockCount = 0;
    physics.lastTorque = 0;
    physics.scBoostKpa = 0;
    physics.actualVvtIntake  = 0; physics.vvtIntIn = 0; physics.vvtErrIn = 0;
    physics.actualVvtExhaust = 0; physics.vvtIntEx = 0; physics.vvtErrEx = 0;
    physics.effectiveWgDuty = 0; physics.clBoostIntegral = 0; physics.clBoostPrevErr = 0;
    physics.filteredMapKpa = -1; physics.accelCurrentMs = 0; physics.accelBlend = 0; physics.accelTimer = 0;
    prevActualTps = 0;
    ecu.disabledCylinders = new Set();
    ecu.meltedCoils       = new Set();
    coilMeltAccumulator   = 0;
    prevWasCranking = false;
    postCrankEnrichActive = false; postCrankEnrichInit = 1.0;
    postCrankEnrichCurrent = 1.0; postCrankCyclesAccum = 0.0; postCrankArmed = false;
    if (typeof updateMeltedCylinderDisplay === 'function') updateMeltedCylinderDisplay();
    const errBox = document.getElementById('error-box');
    if (errBox) errBox.classList.add('hidden');
    elPower.innerText = T('stop_engine', 'STOP ENGINE');
    elPower.className = "px-6 py-2 rounded font-bold bg-red-600 hover:bg-red-500 text-white shadow-lg shadow-red-900/50 transition-colors";
    if (currentRpm < ecu.maxCrankingRpm) currentRpm = ecu.maxCrankingRpm;
    if (audio) audio.start();
    // Coolant persists through normal stop/start — reset only on shift+click
}

function _doEngineStop(statusEl) {
    if (typeof abortDynoPull === 'function') abortDynoPull();
    if (typeof abortDragRun === 'function') abortDragRun();
    elPower.innerText = T('start_engine', 'START ENGINE');
    elPower.className = "px-6 py-2 rounded font-bold bg-green-600 hover:bg-green-500 text-white shadow-lg shadow-green-900/50 transition-colors";
    if (statusEl) { statusEl.innerText = T('off', 'OFF'); statusEl.className = "text-red-400"; }
    if (audio) audio.spindown();
    isStarterHeld = false;
    prevWasCranking = false;
    // Reset per-start cranking state (floodLevel intentionally persists)
    crankAfrSampled = false;
    crankAfrAccum = 0;
    crankAfrDt = 0;
    crankPenaltyApplied = false;
    crankTimeBonus = 0;
    isFloodClear = false;
    simpleGracePeriod = 0;
    postStartPenalty = 0;
    postStartPenaltyInit = 0;
    postStartPenaltyDur = 0;
    postStartPenaltyTimer = 0;
    postCrankEnrichActive = false; postCrankEnrichInit = 1.0;
    postCrankEnrichCurrent = 1.0; postCrankCyclesAccum = 0.0; postCrankArmed = false;
    knockRetardDeg   = 0;
    knockSensorCount = 0;
    spectrogramData  = [];
    const floodHintEl = document.getElementById('flood-hint');
    if (floodHintEl) floodHintEl.classList.toggle('hidden', floodLevel <= 0);
    const injHintEl = document.getElementById('injector-hint');
    if (injHintEl) injHintEl.classList.add('hidden');
}

if (elPower) {
    elPower.addEventListener('click', (e) => {
        // Shift+click = hard reset (clears all state including coolant temp)
        if (e.shiftKey) {
            resetEngineState(true);
            return;
        }

        const statusEl = document.getElementById('data-status');
        if (physics.engineBlown || (!isEngineRunning && statusEl && statusEl.innerText.includes("STALLED"))) {
            resetEngineState(false); // clear error but keep coolant
            return;
        }

        if (typeof updateIdleSettings === 'function') updateIdleSettings();

        // Block engine start while timing light mode is active
        if (!isEngineRunning && ecu.timingLightMode) return;

        // In hold mode, starting is handled by mousedown — click only processes stop.
        // Also swallow the click that fires after the hold-mode mousedown release.
        if (starterMode === 'hold' && !isEngineRunning) return;
        if (_holdStartClickConsumed) { _holdStartClickConsumed = false; return; }

        isEngineRunning = !isEngineRunning;
        if (isEngineRunning) {
            _doEngineStart();
        } else {
            _doEngineStop(statusEl);
        }
    });

    // Hold-to-engage: mousedown starts the starter, mouseup/mouseleave releases it
    elPower.addEventListener('mousedown', (e) => {
        if (starterMode !== 'hold') return;
        if (isEngineRunning) return; // click will handle stop
        if (e.button !== 0) return;
        const statusEl = document.getElementById('data-status');
        if (physics.engineBlown || (statusEl && statusEl.innerText.includes("STALLED"))) {
            resetEngineState(false);
            return;
        }
        if (typeof updateIdleSettings === 'function') updateIdleSettings();
        if (ecu.timingLightMode) return;
        isStarterHeld = true;
        isEngineRunning = true;
        _holdStartClickConsumed = true; // the upcoming mouseup will fire a click — ignore it
        _doEngineStart();
    });

    const _releaseStarter = () => { isStarterHeld = false; };
    elPower.addEventListener('mouseup', _releaseStarter);
    elPower.addEventListener('mouseleave', _releaseStarter);
    elPower.addEventListener('touchstart', (e) => {
        if (starterMode !== 'hold' || isEngineRunning) return;
        if (typeof updateIdleSettings === 'function') updateIdleSettings();
        if (ecu.timingLightMode) return;
        isStarterHeld = true;
        isEngineRunning = true;
        _doEngineStart();
    }, { passive: true });
    elPower.addEventListener('touchend', _releaseStarter);
}

function triggerCoilMelt() {
    const mode     = ecu.ignitionMode;
    const cylCount = sampleEngine.global_specs.cylinders;
    const meltedCylsList = [];

    if (mode === "distributor") {
        if (ecu.meltedCoils.has("dist")) return; // already gone
        ecu.meltedCoils.add("dist");
        for (let i = 1; i <= cylCount; i++) { ecu.disabledCylinders.add(i); meltedCylsList.push(i); }

    } else if (mode === "wasted_spark") {
        const pairs   = ecu.getCoilPairs(firingOrderArr, cylCount);
        const healthy = pairs.map((_, i) => i).filter(i => !ecu.meltedCoils.has(i));
        if (!healthy.length) return;
        const victim  = healthy[Math.floor(Math.random() * healthy.length)];
        ecu.meltedCoils.add(victim);
        pairs[victim].forEach(c => { ecu.disabledCylinders.add(c); meltedCylsList.push(c); });

    } else { // sequential_cop
        const healthy = [];
        for (let i = 1; i <= cylCount; i++) if (!ecu.disabledCylinders.has(i)) healthy.push(i);
        if (!healthy.length) return;
        const victim = healthy[Math.floor(Math.random() * healthy.length)];
        ecu.meltedCoils.add(victim);
        ecu.disabledCylinders.add(victim);
        meltedCylsList.push(victim);
    }

    if (typeof updateMeltedCylinderDisplay === 'function') updateMeltedCylinderDisplay();
    if (typeof showCoilMeltWarning === 'function') showCoilMeltWarning(meltedCylsList);
}

// --- Cranking AFR penalty helpers ---
function computeCrankTimeBonus(afr) {
    if (afr >= 11.5 && afr <= 15.5) return 0;
    if (afr < 11.5) {
        // Rich: 11.5→0 s, 11.0→1 s, 10.0→3 s (flood threshold)
        return Math.max(0, Interpolation.interpolate1D(afr, [10.0, 11.0, 11.5], [3, 1, 0]));
    }
    // Lean: 15.5→0 s, 17.0→1.5 s, 19.0→3 s, cap at 5 s
    return Math.min(5, Math.max(0, (afr - 15.5) * 1.0));
}

function computeRichPenalty(afr) {
    // 0% at AFR 11.5, 10% at AFR 9.5
    return Math.max(0, Math.min(0.10, 0.10 * (11.5 - afr) / (11.5 - 9.5)));
}

function computePenaltyDuration(afr) {
    // 0 s at 11.5, 2 s at 11.0, 5 s at 10.0
    return Math.max(0, Math.min(5, Interpolation.interpolate1D(afr, [10.0, 11.0, 11.5], [5, 2, 0])));
}

function physicsLoop(timestamp) {
    let rawDt = (timestamp - lastTime) / 1000;
    lastTime = timestamp;
    if (rawDt > 0.1) rawDt = 0.1; // Cap extreme lag
    
    // To maintain stability at 3x or 10x drag speeds, we subdivide the frame into micro-steps
    const effectiveSimSpeed = dragManualMode ? 1 : dragSimSpeed;
    let timeMultiplier = (appMode === 'drag' && dragPhase !== 'idle' && dragPhase !== 'finished') ? effectiveSimSpeed : 1.0;
    let totalDt = rawDt * timeMultiplier;
    const MAX_STEP = 0.01; 
    let steps = Math.ceil(totalDt / MAX_STEP);
    let dt = totalDt / steps;

    // Rate of TPS change (%/s) for accel enrichment; computed at frame level using rawDt
    const dTps = rawDt > 0 ? Math.max(0, (actualTps - prevActualTps) / rawDt) : 0;
    prevActualTps = actualTps;

    let result = {};
    let isCranking = false;
    let twoStepCut = false;

    // Frame-level idle targets (needed by micro-step loop, status display, and PID indicator)
    const liveIdleTargetRpm = (ecu && ecu.idleTargetRpmAxis)
        ? Interpolation.interpolate1D(currentCoolantC ?? 90, ecu.idleTargetRpmAxis, ecu.idleTargetRpmCurve)
        : 850;
    if (ecu) ecu.liveIdleTargetRpm = liveIdleTargetRpm;
    const _frameDutyPct = (ecu && ecu.idleTargetRpmAxis)
        ? Interpolation.interpolate1D(currentCoolantC ?? 90, ecu.idleDutyCoolantAxis, ecu.idleDutyCurve)
        : 45;
    const liveIdleTps = (_frameDutyPct / 100) * (ecu ? ecu.maxIdleTpsPct : 10);

    // Physics Micro-Stepper
    for (let s = 0; s < steps; s++) {

        let targetTps = (isPedalHeldDown && elSliderTps) ? parseFloat(elSliderTps.value) : 0;
        let crankDecay = 0;
        isCranking = false;
        let dynoInertiaMultiplier = 1.0;
        let dragApplyingWheelForce = false; // Prevents natural neutral revving if wheels are connected
        twoStepCut = false;
        isFloodClear = false;

        if (isEngineRunning) {
            engineTimer += dt;

            const effectiveCrankTime = advancedMode ? (ecu.starterCrankTime + crankTimeBonus) : 2.0;

            if ((advancedMode && floodLevel > 0) || engineTimer < effectiveCrankTime) {
                isCranking = true;
                currentRpm = ecu.maxCrankingRpm + ((Math.random() * 10) - 5);
                const _crankCoolant = (currentCoolantC !== null) ? currentCoolantC : 20;
                targetTps = (Interpolation.interpolate1D(_crankCoolant, ecu.crankingIdleDutyAxis, ecu.crankingIdleDutyCurve) / 100) * ecu.maxIdleTpsPct;

                // Hold-to-engage: if user releases before engine fires, abort
                if (starterMode === 'hold' && !isStarterHeld) {
                    isEngineRunning = false;
                    currentRpm = 0;
                    if (audio) audio.stop();
                    if (elPower) {
                        elPower.innerText = "START ENGINE";
                        elPower.className = "px-6 py-2 rounded font-bold bg-green-600 hover:bg-green-500 text-white shadow-lg shadow-green-900/50 transition-colors";
                    }
                    crankAfrSampled = false; crankAfrAccum = 0; crankAfrDt = 0;
                    crankPenaltyApplied = false; crankTimeBonus = 0; simpleGracePeriod = 0;
                    prevWasCranking = false;
                    break;
                }

                // Auto-stop after 5 s of stuck flooding — starter gives up, hint appears (advanced only)
                if (advancedMode && floodLevel > 0 && engineTimer >= 5.0) {
                    isEngineRunning = false;
                    currentRpm = 0;
                    if (audio) audio.stop();
                    if (elPower) {
                        elPower.innerText = "START ENGINE";
                        elPower.className = "px-6 py-2 rounded font-bold bg-green-600 hover:bg-green-500 text-white shadow-lg shadow-green-900/50 transition-colors";
                    }
                    const _statusEl = document.getElementById('data-status');
                    if (_statusEl) { _statusEl.innerText = T('flooded', 'FLOODED'); _statusEl.className = "text-red-400 font-bold"; }

                    const _hintEl = document.getElementById('flood-hint');
                    if (_hintEl) _hintEl.classList.remove('hidden');
                    crankAfrSampled = false;
                    crankAfrAccum = 0;
                    crankAfrDt = 0;
                    crankPenaltyApplied = false;
                    crankTimeBonus = 0;
                    isFloodClear = false;
                    break;
                }

                // Flood clear: user holds WOT while cranking → cut fuel, purge cylinders (advanced only)
                const rawUserTps = isPedalHeldDown ? parseFloat(elSliderTps.value) : 0;
                if (advancedMode && floodLevel > 0 && rawUserTps >= 80) {
                    isFloodClear = true;
                    const revsPerSec = currentRpm / 60.0;
                    floodLevel = Math.max(0, floodLevel - revsPerSec * (1.0 / 10.0) * dt);
                    if (floodLevel <= 0) {
                        // Flood cleared — reset so engine re-accumulates AFR cleanly on start
                        crankTimeBonus = 0;
                        crankAfrSampled = false;
                        crankAfrAccum = 0;
                        crankAfrDt = 0;
                        crankingAfrObserved = 14.7;
                        crankPenaltyApplied = false;
                        const _clearHintEl = document.getElementById('flood-hint');
                        if (_clearHintEl) _clearHintEl.classList.add('hidden');
                    }
                }
            } else {
                let timeSinceStart = engineTimer - effectiveCrankTime;
                if (timeSinceStart < ecu.crankingDecayTime) {
                    crankDecay = 1.0 - (timeSinceStart / ecu.crankingDecayTime);
                }

                // In non-advanced mode, arm the 5-s grace period the first time we leave cranking
                if (!advancedMode && simpleGracePeriod === 0) {
                    simpleGracePeriod = 5.0;
                }

                // Post-crank enrichment — arm once on first post-crank frame, then decay each micro-step
                if (!postCrankArmed) {
                    postCrankArmed = true;
                    const _enrichCoolant = (currentCoolantC !== null) ? currentCoolantC : 20;
                    const _enrichInit = Interpolation.interpolate1D(_enrichCoolant, ecu.postCrankEnrichAxis, ecu.postCrankEnrichCurve);
                    if (_enrichInit > 1.001) {
                        postCrankEnrichActive  = true;
                        postCrankEnrichInit    = _enrichInit;
                        postCrankEnrichCurrent = _enrichInit;
                        postCrankCyclesAccum   = 0.0;
                    }
                }
                if (postCrankEnrichActive) {
                    const cyclesPerSec = currentRpm / 120.0; // one 720° cycle = 2 revs (4-stroke)
                    postCrankCyclesAccum += cyclesPerSec * dt;
                    const t = Math.min(1.0, postCrankCyclesAccum / Math.max(1, ecu.postCrankDecayCycles));
                    postCrankEnrichCurrent = postCrankEnrichInit + (1.0 - postCrankEnrichInit) * t;
                    if (t >= 1.0) { postCrankEnrichActive = false; postCrankEnrichCurrent = 1.0; }
                }

                // Trigger no-start: if effective trigger angle error ≥ 60° the engine can't fire — kill it cleanly.
                // Effective error accounts for ignition mode periodicity (wasted_spark wraps at 360°,
                // distributor wraps at 720/cylinders°, sequential_cop uses raw error with no symmetry).
                if (advancedMode && !crankAfrSampled) {
                    const _rawErr = Math.abs((window.engineTriggerBaseOffset ?? 0) - (ecu.triggerAngleOffset ?? 0));
                    let _absTrigErr = _rawErr;
                    if (ecu.ignitionMode === 'wasted_spark' || ecu.ignitionMode === 'wasted_spark_individual') {
                        const m = _rawErr % 360;
                        _absTrigErr = Math.min(m, 360 - m);
                    } else if (ecu.ignitionMode === 'distributor') {
                        const _fi = 720 / (sampleEngine.global_specs.cylinders || 4);
                        const m = _rawErr % _fi;
                        _absTrigErr = Math.min(m, _fi - m);
                    }
                    if (_absTrigErr >= 60) {
                        // Backfire pops at the kill moment — intensity scales with error.
                        // Sequential at 340-360°: 3-bang exhaust backfire burst (firing on exhaust stroke).
                        if (audio) {
                            const _isSeqBackfire = ecu.ignitionMode === 'sequential_cop' && _rawErr >= 340;
                            if (_isSeqBackfire) {
                                audio.triggerPop(0.90);
                                audio.triggerPop(0.80);
                                audio.triggerPop(0.70);
                            } else {
                                const _intensityFrac = Math.min(1, (_absTrigErr - 60) / 240);
                                audio.triggerPop(0.3 + _intensityFrac * 0.4);
                            }
                        }
                        isEngineRunning = false;
                        currentRpm = 0;
                        if (audio) audio.stop();
                        if (elPower) {
                            elPower.innerText = T('start_engine', 'START ENGINE');
                            elPower.className = "px-6 py-2 rounded font-bold bg-green-600 hover:bg-green-500 text-white shadow-lg shadow-green-900/50 transition-colors";
                        }
                        const _statusEl = document.getElementById('data-status');
                        if (_statusEl) { _statusEl.innerText = T('no_start_trigger', 'NO START: TRIGGER ANGLE'); _statusEl.className = "text-yellow-400 font-bold"; }
                        crankAfrSampled = false; crankAfrAccum = 0; crankAfrDt = 0;
                        crankPenaltyApplied = false; crankTimeBonus = 0;
                        prevWasCranking = false;
                        break;
                    }
                }

                // Evaluate time-averaged cranking AFR once, at the end of the base 2-s window (advanced only)
                if (advancedMode && !crankAfrSampled && crankAfrDt > 0) {
                    crankingAfrObserved = crankAfrAccum / crankAfrDt;

                    // Lean no-start: too little fuel to fire
                    if (crankingAfrObserved >= 17.0) {
                        isEngineRunning = false;
                        currentRpm = 0;
                        if (audio) audio.stop();
                        if (elPower) {
                            elPower.innerText = T('start_engine', 'START ENGINE');
                            elPower.className = "px-6 py-2 rounded font-bold bg-green-600 hover:bg-green-500 text-white shadow-lg shadow-green-900/50 transition-colors";
                        }
                        const _statusEl = document.getElementById('data-status');
                        if (_statusEl) { _statusEl.innerText = T('no_start_lean', 'NO START: LEAN'); _statusEl.className = "text-yellow-400 font-bold"; }
                        crankAfrSampled = false; crankAfrAccum = 0; crankAfrDt = 0;
                        crankPenaltyApplied = false; crankTimeBonus = 0;
                        prevWasCranking = false;
                        break;
                    }

                    crankTimeBonus = computeCrankTimeBonus(crankingAfrObserved);
                    if (crankingAfrObserved < 10.0) {
                        const richness = Math.min(1.0, (10.0 - crankingAfrObserved) / 2.5);
                        floodLevel = Math.min(1.0, floodLevel + 0.25 + richness * 0.75);
                    } else {
                        floodLevel = 0;
                        const _hintClear = document.getElementById('flood-hint');
                        if (_hintClear) _hintClear.classList.add('hidden');
                    }
                    crankAfrSampled = true;
                    // If bonus or flood was set, engine re-enters cranking block next frame
                }

                // Apply post-start torque penalty once — only when flood is clear
                if (!crankPenaltyApplied && crankAfrSampled && floodLevel <= 0) {
                    crankPenaltyApplied = true;
                    if (crankingAfrObserved < 11.5) {
                        postStartPenaltyInit = computeRichPenalty(crankingAfrObserved);
                        postStartPenaltyDur  = computePenaltyDuration(crankingAfrObserved);
                        postStartPenalty     = postStartPenaltyInit;
                        postStartPenaltyTimer = 0;
                    }
                }
                
                if (isDynoPullActive) {
                    const dispCc = sampleEngine.global_specs.displacement_cc || 1600;
                    dynoInertiaMultiplier = Math.max(3.0, (dispCc * 0.0044) - 2.0); 
                    
                    if (dynoPhase === 'hold') {
                        if (dynoMode === 'rpm-lock') {
                            dynoPhase = 'pull';
                        } else {
                            dynoTimer += dt;
                            targetTps = 15;
                            if (currentRpm > dynoStartRpm) {
                                currentRpm -= (currentRpm - dynoStartRpm) * 5 * dt;
                                targetTps = 5;
                            } else if (currentRpm < dynoStartRpm - 50) {
                                targetTps = 25;
                            }
                            if (dynoTimer >= 2.0) {
                                dynoPhase = 'pull';
                                const hpStat = document.getElementById('dyno-stat-hp');
                                const tqStat = document.getElementById('dyno-stat-tq');
                                if (hpStat) hpStat.innerText = `PULLING...`;
                                if (tqStat) tqStat.innerText = `PULLING...`;
                            }
                        }
                    } else if (dynoPhase === 'pull') {
                        if (dynoMode === 'rpm-lock') {
                            const maxTpsEl = document.getElementById('slider-tps');
                            targetTps = maxTpsEl ? parseFloat(maxTpsEl.value) : 100;
                            currentRpm = dynoLockRpm;
                        } else {
                            targetTps = 100;
                        }
                        dynoPullTimer += dt;
                    }
                } 
                else if (appMode === 'drag' && dragPhase !== 'idle' && dragPhase !== 'finished') {
                    
                    // DRAG STRIP LOGIC (Overrides throttle and RPM)
                    let launchRpm = parseFloat(document.getElementById('drag-launch-rpm').value) || 4500;
                    let shiftRpm = parseFloat(document.getElementById('drag-shift-rpm').value) || 7200;
                    let shiftDelay = parseFloat(document.getElementById('drag-shift-delay').value) || 0.3;
                    let massKg = (parseFloat(document.getElementById('drag-weight').value) || 2270) * 0.453592;
                    const TIRE_MU = {
                        'Economy':      { launch: 0.5, peak: 0.8 },
                        'Street':       { launch: 0.6, peak: 1.0 },
                        'Summer':       { launch: 0.9, peak: 1.2 },
                        'Drag Radials': { launch: 1.2, peak: 1.8 },
                        'Drag Slicks':  { launch: 1.6, peak: 2.5 },
                    };
                    const tireName = document.getElementById('drag-treadwear').value || 'Economy';
                    const tireMu = TIRE_MU[tireName] || TIRE_MU['Economy'];

                    let validGears = currentTransmission.gears.filter(g => g > 0);
                    if (validGears.length === 0) validGears = [1.0]; // Failsafe
                    let finalDrive = currentTransmission.final_drive || 1.0;
                    let tireRadius = (22.7 * 0.0254) / 2;
                    let mph = dragVel * 2.237;
                    let mu;
                    if (mph < 5) {
                        mu = tireMu.launch * (0.5 + 0.5 * (mph / 5));
                    } else {
                        mu = tireMu.launch + (tireMu.peak - tireMu.launch) * Math.min(1.0, (mph - 5) / 35);
                    }
                    if (launchSpinPenaltyActive) {
                        launchSpinPenaltyTimer += dt;
                        if (launchSpinPenaltyTimer >= 4.0 || mph >= 40) {
                            launchSpinPenaltyActive = false;
                        } else {
                            mu *= 0.5;
                        }
                    }
                    let fMax = massKg * 9.81 * mu;

                    if (dragPhase.startsWith('staging')) {
                        dragTimer += dt;
                        targetTps = dragManualMode ? (dragThrottlePressed ? 100 : 0) : 100; // Revving on the transbrake
                        
                        if (currentRpm > launchRpm) {
                            twoStepCut = true;
                            currentRpm = launchRpm + (Math.random() * 50);
                        }
                        
                        if (dragTimer >= 0.5) {
                            dragTimer = 0;
                            if (dragPhase === 'staging1') { dragPhase = 'staging2'; updateDragHud('AMBER 1', '#fbbf24'); if (audio) audio.triggerAmberBeep(); }
                            else if (dragPhase === 'staging2') { dragPhase = 'staging3'; updateDragHud('AMBER 2', '#fbbf24'); if (audio) audio.triggerAmberBeep(); }
                            else if (dragPhase === 'staging3') { dragPhase = 'staging4'; updateDragHud('AMBER 3', '#fbbf24'); if (audio) audio.triggerAmberBeep(); }
                            else if (dragPhase === 'staging4') {
                                if (dragManualMode || document.getElementById('chk-reaction-mode')?.checked) {
                                    dragPhase = 'waitForLaunch';
                                    dragReactionTimer = 0;
                                    updateDragHud('GO!!!', '#4ade80');
                                    if (audio) audio.triggerGoBeep();
                                } else {
                                    dragPhase = 'pull';
                                    updateDragHud('GO!!!', '#4ade80');
                                    if (audio) audio.triggerGoBeep();
                                }
                            }
                        }
                    }
                    else if (dragPhase === 'waitForLaunch') {
                        dragApplyingWheelForce = false;
                        targetTps = dragManualMode ? (dragThrottlePressed ? 100 : 0) : 100;
                        if (currentRpm > launchRpm) {
                            twoStepCut = true;
                            currentRpm = launchRpm + (Math.random() * 50);
                        }
                        dragReactionTimer += dt;
                    }
                    else if (dragPhase === 'pull') {
                        dragApplyingWheelForce = true; // Suspend neutral free-rev math
                        targetTps = dragManualMode ? (dragThrottlePressed ? 100 : 0) : 100;

                        // Throttle lift-and-reapply: clear penalty if over 5 mph; under 5 mph
                        // the existing slip detection will re-trigger it if still spinning.
                        if (dragLastTps === 0 && targetTps > 0 && launchSpinPenaltyActive) {
                            launchSpinPenaltyActive = false;
                            launchSpinPenaltyTimer  = 0;
                        }
                        dragLastTps = targetTps;

                        let isSlipping = false;
                        let tqRes = null;

                        // Read clutch config — rating and heat come from engine profile + installed Clutch mod
                        const clutchSlipTime = parseFloat(document.getElementById('drag-clutch-slip-time')?.value) || 0.5;
                        const clutchRating   = physics.clutchTorqueRating;  // lb-ft, OEM × mod multiplier
                        const clutchHeatCapJ = physics.clutchHeatCapJ;      // Joules, OEM × mod multiplier
                        const idleRpm = ecu.liveIdleTargetRpm ?? 850;

                        let currentGearRatio = validGears[dragGear] || validGears[validGears.length - 1];
                        // Raw drivetrain speed — NOT clamped, used for clutch slip calculations
                        let mechRpm = (dragVel / tireRadius) * currentGearRatio * finalDrive * (60 / (2 * Math.PI));

                        // --- CLUTCH ENGAGEMENT & SLIP ---
                        if (dragManualMode) {
                            if (dragClutchPressed) {
                                clutchEngagement = 0.0;
                            } else if (dragGear === 0) {
                                if (!clutchFailed) {
                                    dragManualSlipTimer += dt;
                                    const t = Math.min(1.0, clutchSlipTime < 0.01 ? 1.0 : dragManualSlipTimer / clutchSlipTime);
                                    clutchEngagement = t * t * (3 - 2 * t);
                                } else {
                                    clutchEngagement = 0.05;
                                }
                            } else {
                                clutchEngagement = clutchFailed ? 0.05 : 1.0;
                            }
                        } else if (dragGear === 0) {
                            if (!clutchFailed) {
                                clutchSlipTimer += dt;
                                if (clutchSlipTime < 0.01) {
                                    clutchEngagement = 1.0;
                                } else {
                                    const t = Math.min(1.0, clutchSlipTimer / clutchSlipTime);
                                    clutchEngagement = t * t * (3 - 2 * t); 
                                }
                            } else {
                                clutchEngagement = 0.05;
                            }
                        } else {
                            clutchEngagement = 1.0; 
                        }

                        const slipFraction = 1.0 - clutchEngagement;
                        let engineRpm;
                        if (dragManualMode && dragClutchPressed) {
                            engineRpm = currentRpm;
                        } else {
                            const freeRevRpm = Math.max(mechRpm, launchRpm);
                            engineRpm = mechRpm * clutchEngagement + freeRevRpm * slipFraction;
                        }

                        let stalling = false;
                        if (currentRpm < idleRpm && clutchEngagement > 0.01 && (dragGear === 0 || dragManualMode)) {
                            stalling = true;
                            currentRpm = Math.max(0, currentRpm - 2000 * dt);
                            if (currentRpm <= 0) {
                                dragPhase = 'finished';
                                _setDragSettingsLocked(false);
                                updateDragHud('STALLED', '#ef4444');
                            }
                        }

                        let physRpm = Math.max(currentRpm, 900);
                        let appliedForce = 0;

                        if (!stalling) {
                            tqRes = physics.calculateTorqueAtRPM(physRpm, targetTps, dt, 0, 0, false, true, false, false, nitrousActive);
                            let engineTorque = tqRes.error ? 0 : tqRes.torque;
                            dragTorqueDisplay = engineTorque;
                            if (engineTorque > dragMaxTq) dragMaxTq = engineTorque;
                            const currentHp = (engineTorque * physRpm) / 5252;
                            if (currentHp > dragMaxHp) dragMaxHp = currentHp;

                            // --- CLUTCH TORQUE CAPACITY ---
                            // If engine torque exceeds static capacity (clutchRating * engagement), it breaks loose into kinetic slip.
                            const maxClutchTorque = clutchRating * clutchEngagement;
                            let clampedTorque = engineTorque;

                            if (clutchFailed) {
                                isClutchSlipping = true;
                                clampedTorque = maxClutchTorque * 0.05;
                            } else if (engineTorque > clutchRating) {
                                // Overtorque: engine exceeds full clutch capacity — kinetic slip
                                isClutchSlipping = true;
                                clampedTorque = Math.min(engineTorque, maxClutchTorque * 0.55);
                            } else {
                                // Normal engagement ramp: clutch builds grip proportionally — not full slip
                                isClutchSlipping = clutchEngagement < 0.999; // still "slipping" for heat/UI
                                clampedTorque = engineTorque * clutchEngagement;
                            }

                            let wheelForce = (clampedTorque * 1.3558 * currentGearRatio * finalDrive * 0.85) / tireRadius;
                            appliedForce = wheelForce;

                            if (wheelForce > fMax) {
                                isSlipping = true; // Tire slip
                                appliedForce = fMax;
                                if (mph < 5 && !launchSpinPenaltyActive) {
                                    launchSpinPenaltyActive = true;
                                    launchSpinPenaltyTimer  = 0;
                                }
                            }

                            // Update Engine RPM for next frame
                            // isActuallyFreeSlipping: only when clutch genuinely can't hold (overtorque or failed)
                            const isActuallyFreeSlipping = (engineTorque > clutchRating) || clutchFailed;
                            if (isActuallyFreeSlipping || wheelForce > fMax) {
                                // Engine decoupled — allow RPM to rise
                                const rpmRiseRate = isActuallyFreeSlipping ? 1200 : 3500;
                                currentRpm = Math.max(currentRpm, engineRpm) + rpmRiseRate * dt;
                                if (currentRpm > (ecu.revLimit || sampleEngine?.mechanical_limits?.redline_rpm || 8500)) currentRpm = (ecu.revLimit || sampleEngine?.mechanical_limits?.redline_rpm || 8500);
                            } else if (dragManualMode && dragClutchPressed) {
                                const _dispScale = sampleEngine.global_specs.displacement_cc / 1000;
                                const _mechFric = sampleEngine.mechanical_limits.mechanical_friction_coeff || 0.008;
                                const _friction = (_dispScale * 6) + (currentRpm * _mechFric * _dispScale * 0.5);
                                const _netTq = (tqRes.error ? 0 : tqRes.torque) - _friction;
                                const _inertia = Math.max(0.05, (sampleEngine && sampleEngine.mechanical_limits && sampleEngine.mechanical_limits.stock_flywheel_inertia_kgm2) ? sampleEngine.mechanical_limits.stock_flywheel_inertia_kgm2 : 0.2);
                                const _alpha = (_netTq * 1.3558 / (_inertia * dynoInertiaMultiplier)) * (60 / (2 * Math.PI));
                                currentRpm = Math.max(0, currentRpm + _alpha * dt);
                            } else if (clutchEngagement >= 0.999) {
                                currentRpm = mechRpm; // Fully locked
                            } else {
                                // Normal clutch engagement: pull RPM toward mechRpm proportional to engagement
                                // Sounds like the engine loading up as the clutch grabs
                                const pullRate = clutchEngagement * 6000; // rpm/s, zero when disengaged
                                const drop = Math.min(Math.max(0, currentRpm - mechRpm), pullRate * dt);
                                currentRpm = Math.max(mechRpm, currentRpm - drop);
                            }

                            // --- CLUTCH HEAT ---
                            if (isClutchSlipping) {
                                const slipOmega = Math.abs(currentRpm - mechRpm) * (Math.PI / 30); // rad/s
                                // Heat (Joules) = Transmitted Torque * Slip Speed
                                const slipPowerW = (clampedTorque * 1.3558) * slipOmega;
                                clutchHeat += (slipPowerW * dt / clutchHeatCapJ) * 100 * 0.7;                            } else {
                                const coolingW = 200 + dragVel * 40;
                                clutchHeat = Math.max(0, clutchHeat - (coolingW * dt / clutchHeatCapJ) * 100);
                            }
                            clutchHeat = Math.min(100, clutchHeat);
                            if (clutchHeat >= 100 && !clutchFailed) clutchFailed = true;
                        }

                        if (!dragIsShifting) {
                            let fDrag = 0.5 * 1.225 * Math.pow(dragVel, 2) * 0.32 * 1.9;
                            let accel = (appliedForce - fDrag) / massKg;
                            dragGForce = stalling ? 0 : accel / 9.81;
                            dragVel = Math.max(0, dragVel + accel * dt);
                            dragDist += dragVel * dt;

                            if (!stalling && !dragManualMode) {
                                const noShiftOnSlip = document.getElementById('chk-no-shift-on-slip')?.checked;
                                if (currentRpm >= shiftRpm && dragGear < validGears.length - 1 && !(noShiftOnSlip && isSlipping)) {
                                    dragIsShifting = true;
                                    dragShiftTimer = 0;
                                }
                            }
                        } else {
                            if (dragManualMode) {
                                // Cancel any in-progress auto-shift if mode was toggled mid-run
                                dragIsShifting = false;
                            } else {
                                targetTps = 0;
                                dragShiftTimer += dt;
                                dragTorqueDisplay = 0;

                                let fDrag = 0.5 * 1.225 * Math.pow(dragVel, 2) * 0.32 * 1.9;
                                let accel = -fDrag / massKg;
                                dragGForce = accel / 9.81;

                                dragVel += accel * dt;
                                dragDist += dragVel * dt;

                                currentRpm -= (3000 * dt);
                                if (currentRpm < 1000) currentRpm = 1000;

                                if (dragShiftTimer >= shiftDelay) {
                                    dragIsShifting = false;
                                    dragGear++;
                                }
                            }
                        }

                        // Tire screech: only when tires are slipping (not just clutch slip)
                        if (audio) audio.setScreech(isSlipping && !isClutchSlipping);
                        const slipInd = document.getElementById('drag-slip-ind');
                        if (slipInd) slipInd.classList.toggle('hidden', !isSlipping);
                        document.getElementById('drag-clutch-slip-ind')?.classList.toggle('hidden', !(isClutchSlipping && !clutchFailed));
                        document.getElementById('drag-clutch-fail-ind')?.classList.toggle('hidden', !clutchFailed);

                        // Clutch heat gauge (color: green → amber → red)
                        const _chBar = document.getElementById('clutch-heat-bar');
                        if (_chBar) {
                            _chBar.style.width = clutchHeat.toFixed(0) + '%';
                            _chBar.style.backgroundColor = clutchHeat >= 70 ? '#ef4444' : clutchHeat >= 40 ? '#f59e0b' : '#22c55e';
                        }
                        const _chLabel = document.getElementById('clutch-heat-label');
                        if (_chLabel) _chLabel.innerText = `CLUTCH: ${clutchHeat.toFixed(0)}%`;

                        dragTime += dt;
                        const splits = {"60ft": 18.28, "330ft": 100.58, "18Mile": 201.16, "1000ft": 304.8, "14Mile": 402.33};
                        for (let [name, distThresh] of Object.entries(splits)) {
                            if (dragDist >= distThresh && !dragSplits[name]) {
                                dragSplits[name] = dragTime;
                                const splitEl = document.getElementById(`split-${name}`);
                                if (splitEl) {
                                    splitEl.innerText = `${dragTime.toFixed(3)}s @ ${displaySpeed(dragVel)}`;
                                    splitEl.classList.replace('text-white', 'text-green-400');
                                }
                            }
                        }

                        if (dragDist >= 402.33 || tqRes?.error) {
                            dragPhase = 'finished';
                            _setDragSettingsLocked(false);
                            if (audio) audio.setScreech(false);
                            const slipInd = document.getElementById('drag-slip-ind');
                            if (slipInd) slipInd.classList.add('hidden');
                            document.getElementById('drag-clutch-slip-ind')?.classList.add('hidden');
                            document.getElementById('drag-clutch-fail-ind')?.classList.add('hidden');
                            if (tqRes?.error) {
                                updateDragHud(`ENGINE BLEW UP! PASS ABORTED`, '#ef4444');
                            } else {
                                updateDragHud(`FINISH: ${dragTime.toFixed(3)}s @ ${displaySpeed(dragVel).toUpperCase()}`, '#4ade80');
                                const submitWrap = document.getElementById('leaderboard-submit-wrap');
                                
                                // ELIGIBILITY CHECKS
                                const weightDef = sampleEngine.global_specs?.default_vehicle_weight_lbs || 2270;
                                const w = parseFloat(document.getElementById('drag-weight')?.value) || 2270;
                                const sDel = parseFloat(document.getElementById('drag-shift-delay')?.value) || 0.3;
                                
                                const isWeightOk = Math.abs(w - weightDef) < 0.1;
                                const isShiftOk = sDel >= 0.1;

                                if (submitWrap) {
                                    if (!advancedMode) {
                                        const reasonEl = document.getElementById('drag-lb-reason');
                                        if (reasonEl) {
                                            reasonEl.classList.remove('hidden');
                                            reasonEl.innerText = `NO LEADERBOARDS FOR SIMPLE MODE!`;
                                        }
                                    } else if (isWeightOk && isShiftOk) {
                                        submitWrap.classList.remove('hidden');
                                        // Auto-fill from local storage or shopify ID
                                        const savedName = localStorage.getItem('lb_preferred_name');
                                        const nameInput = document.getElementById('lb-display-name');
                                        if (nameInput) {
                                            nameInput.value = savedName || window.shopifyCustomer?.name || window.shopifyCustomer?.email || "";
                                        }
                                    } else {
                                        // Pass was successful but disqualified from leaderboard
                                        const reasonEl = document.getElementById('drag-lb-reason');
                                        if (reasonEl) {
                                            reasonEl.classList.remove('hidden');
                                            if (!isWeightOk) reasonEl.innerText = `DISQUALIFIED: VEHICLE WEIGHT MUST BE ${weightDef} LBS`;
                                            else if (!isShiftOk) reasonEl.innerText = `DISQUALIFIED: SHIFT DELAY MUST BE >= 0.1s`;
                                        }
                                    }
                                }
                            }
                            if (typeof exportDatalog === 'function') exportDatalog('Drag');
                        }
                    }
                } 
                else {
                    const userTps = isPedalHeldDown ? parseFloat(elSliderTps.value) : 0;
                    const liveIdleDutyPct = Interpolation.interpolate1D(currentCoolantC ?? 90, ecu.idleDutyCoolantAxis, ecu.idleDutyCurve);
                    const stepIdleTps = (liveIdleDutyPct / 100) * ecu.maxIdleTpsPct;
                const _decayCoolant = (currentCoolantC !== null) ? currentCoolantC : 20;
                const _crankingDutyDecay = (Interpolation.interpolate1D(_decayCoolant, ecu.crankingIdleDutyAxis, ecu.crankingIdleDutyCurve) / 100) * ecu.maxIdleTpsPct;
                let baseTps = stepIdleTps + ((_crankingDutyDecay - stepIdleTps) * crankDecay);
                    const inIdleControl = currentRpm <= liveIdleTargetRpm + ecu.idleRpmUpperLimit;
                    targetTps = inIdleControl ? Math.max(baseTps, userTps) : userTps;
                }
            }
            targetTps = Math.max(0, Math.min(100, targetTps));
        }

        // --- BOOST CUT THROTTLE INTERVENTION ---
        if (physics.boostCutActive) {
            targetTps = 0;
        }
        
        const isActiveDrag = appMode === 'drag' && dragPhase !== 'idle' && dragPhase !== 'finished';
        let tpsSlewRate = isActiveDrag ? 100 : 10;
        actualTps += (targetTps - actualTps) * tpsSlewRate * dt;

        if (isEngineRunning || currentRpm > 0) {
            let pidTimingAdj = 0;
            if (isEngineRunning && !isPedalHeldDown && !isDynoPullActive && !isActiveDrag && advancedMode && ecu.idleIgnEnabled && !isCranking) {
                let error = liveIdleTargetRpm - currentRpm;
                idleIntegral += error * dt;
                let derivative = (error - prevIdleError) / dt;
                pidTimingAdj = (error * ecu.idleIgnP) + (idleIntegral * ecu.idleIgnI) + (derivative * ecu.idleIgnD);
                pidTimingAdj = Math.max(-12, Math.min(12, pidTimingAdj));
                prevIdleError = error;
            } else {
                idleIntegral = 0;
                prevIdleError = 0;
            }

            let isDragStaging = (appMode === 'drag' && (dragPhase === 'staging1' || dragPhase === 'staging2' || dragPhase === 'staging3' || dragPhase === 'staging4' || dragPhase === 'waitForLaunch'));

            // --- NITROUS ACTIVATION LOGIC ---
            if (ecu.nitrousEnabled) {
                if (ecu.nitrousActivationMode === 'switch') {
                    nitrousActive = nitrousSwitchHeld;
                } else {
                    // ECU control: all conditions must be met
                    const afrSafe = lastFrameAfr < ecu.nitrousAfrCutLean;
                    nitrousActive = (
                        actualTps >= ecu.nitrousMinTps &&
                        currentRpm >= ecu.nitrousMinRpm &&
                        currentRpm <= ecu.nitrousMaxRpm &&
                        (result.mapKpa || 101.3) <= ecu.nitrousMaxMapKpa &&
                        afrSafe
                    );
                }
            } else {
                nitrousActive = false;
            }

            // Tick calibration timing adder (counts down in real seconds via dt)
            if (calTimingAdderRemainSec > 0) {
                calTimingAdderRemainSec = Math.max(0, calTimingAdderRemainSec - dt);
                if (calTimingAdderRemainSec === 0) {
                    calTimingAdderDeg = 0;
                    const cdEl = document.getElementById('cal-timing-countdown');
                    if (cdEl) { cdEl.textContent = ''; cdEl.classList.add('hidden'); }
                }
            }

            result = physics.calculateTorqueAtRPM(currentRpm, actualTps, dt, pidTimingAdj + calTimingAdderDeg, crankDecay, isCranking, isPedalHeldDown, isDynoPullActive, isDragStaging, nitrousActive, isFloodClear, dTps, knockRetardDeg, postCrankEnrichCurrent);
            
            if (twoStepCut) {
                result.torque = 0;
                if (Math.random() < 0.4) {
                    result.popping = true;
                    result.popIntensity = 0.6 + Math.random() * 0.4;
                }
            }

            if (result.valid) lastFrameAfr = result.actualAfr;

            // O2 transport delay buffer — push every valid frame
            if (result.valid) {
                const _nowMs = performance.now();
                afrHistory.push({ t: _nowMs, afr: result.actualAfr });
                while (afrHistory.length > 1 && _nowMs - afrHistory[0].t > 400) afrHistory.shift();
            }

            // Accumulate time-weighted AFR during the base crank window (not during flood clear)
            if (isCranking && !crankAfrSampled && !isFloodClear && result.valid) {
                crankAfrAccum += result.actualAfr * dt;
                crankAfrDt += dt;
            }

            if (result.error) {
                if (isEngineRunning && physics.engineBlown && audio) audio.triggerMetalBing();
                isEngineRunning = false;
                if (audio) audio.stop();
                if (isDynoPullActive && typeof abortDynoPull === 'function') abortDynoPull();
                const errBox = document.getElementById('error-box');
                if (errBox) {
                    errBox.classList.remove('hidden');
                    errBox.innerText = (result.stalled ? "STALLED: " : "FATAL: ") + result.error;
                }
                
                const statusEl = document.getElementById('data-status');
                if (result.stalled && statusEl) {
                    statusEl.innerText = "STALLED";
                    statusEl.className = "text-red-400 font-bold";
                    const _injHint = document.getElementById('injector-hint');
                    if (_injHint && physics?.installedInjectorSizeCc && ecu &&
                        Math.abs(physics.installedInjectorSizeCc / ecu.injectorSizeCc - 1.0) >= 0.05) {
                        _injHint.classList.remove('hidden');
                    }
                }
                
                if (elPower) {
                    elPower.innerText = physics.engineBlown ? "RESET ENGINE" : "START ENGINE";
                    elPower.className = physics.engineBlown 
                        ? "px-6 py-2 rounded font-bold bg-orange-600 hover:bg-orange-500 text-white shadow-lg shadow-orange-900/50 transition-colors"
                        : "px-6 py-2 rounded font-bold bg-green-600 hover:bg-green-500 text-white shadow-lg shadow-green-900/50 transition-colors";
                }
            }

            if (!isCranking && !dragApplyingWheelForce) {
                let outputTorque = isEngineRunning && result.valid ? result.torque * (advancedMode ? (1 - postStartPenalty) : 1) : 0;
                const dispScale = sampleEngine.global_specs.displacement_cc / 1000;
                const mechFrictionCoeff = sampleEngine.mechanical_limits.mechanical_friction_coeff || 0.008;
                // rpmFrictionScale must NOT be clamped at 0.8 — that caused sub-1.6L engines
                // (singles, twins, small rotaries) to see friction that exceeds their peak torque.
                // Letting it scale naturally with displacement keeps the formula physically correct:
                // friction ≈ dispScale × (C_static + rpm × coeff × 0.5), so larger engines have
                // proportionally more friction and small engines don't get artificially penalised.
                const rpmFrictionScale = dispScale * 0.5;
                let friction = (dispScale * 6) + (currentRpm * mechFrictionCoeff * rpmFrictionScale);

                if (physics.engineBlown) friction += 15 + (currentRpm * 0.02);

                const netTorque = outputTorque - friction;

                if (isDynoPullActive && dynoPhase === 'pull' && dynoMode === 'load' && isEngineRunning) {
                    currentRpm += dynoRpmRate * dt;
                } else if (!(isDynoPullActive && dynoPhase === 'pull' && dynoMode === 'rpm-lock')) {
                    const baseInertia = Math.max(0.05, (sampleEngine && sampleEngine.mechanical_limits && sampleEngine.mechanical_limits.stock_flywheel_inertia_kgm2) ? sampleEngine.mechanical_limits.stock_flywheel_inertia_kgm2 : 0.2);
                    const alphaRpm = (netTorque * 1.3558 / (baseInertia * dynoInertiaMultiplier)) * (60 / (2 * Math.PI));
                    currentRpm += alphaRpm * dt;
                }

                if (result.valid && result.lopeRpmDelta) currentRpm += result.lopeRpmDelta;

                if (currentRpm < 0) currentRpm = 0;

                if (isDynoPullActive && dynoPhase === 'pull' && isEngineRunning && actualTps > 90 && dynoMode !== 'rpm-lock') {
                    let lastRecordedChartRpm = rawDynoData.labels.length > 0 ? rawDynoData.labels[rawDynoData.labels.length - 1] : dynoStartRpm;
                    let updatedCharts = false;
                    while (currentRpm >= lastRecordedChartRpm + 50) {
                        lastRecordedChartRpm += 50;
                        rawDynoData.labels.push(lastRecordedChartRpm);
                        let tq = result.torque;
                        rawDynoData.hp.push((tq * lastRecordedChartRpm) / 5252);
                        rawDynoData.tq.push(tq);
                        rawDynoData.afr.push(result.actualAfr);
                        rawDynoData.boost.push(result.boostPsi || 0);
                        updatedCharts = true;
                    }
                    if (updatedCharts && typeof replotDyno === 'function') replotDyno();
                }
                if (isDynoPullActive && dynoPhase === 'pull' && isEngineRunning && dynoMode !== 'rpm-lock') {
                    if (currentRpm >= ecu.revLimit || result.revLimit || result.error) finishDynoPull(result.error);
                }

            }

            // Dry spindown complete — RPM has coasted to near-zero, silence the audio
            if (!isEngineRunning && currentRpm < 50 && audio && audio.isDrySpindown) {
                audio.stop();
            }

            // In non-advanced grace period, keep RPM from falling below a rough idle
            if (!advancedMode && simpleGracePeriod > 0 && isEngineRunning && !isCranking && currentRpm < 600) {
                currentRpm += (600 - currentRpm) * Math.min(1, dt * 5);
            }

            if (isEngineRunning && currentRpm < 200 && !isCranking && (advancedMode || simpleGracePeriod <= 0)) {
                isEngineRunning = false;
                if (audio) audio.stop();
                if (typeof abortDynoPull === 'function') abortDynoPull();
                if (appMode === 'drag' && typeof abortDragRun === 'function') abortDragRun();
                const errBox = document.getElementById('error-box');
                if (errBox) {
                    errBox.classList.remove('hidden');
                    errBox.innerText = T('stalled_rpm', 'STALLED: RPM dropped below cranking speed.');
                }
                const statusEl = document.getElementById('data-status');
                if (statusEl) {
                    statusEl.innerText = T('stalled', 'STALLED');
                    statusEl.className = "text-red-400 font-bold";
                    const _injHint = document.getElementById('injector-hint');
                    if (_injHint && physics?.installedInjectorSizeCc && ecu &&
                        Math.abs(physics.installedInjectorSizeCc / ecu.injectorSizeCc - 1.0) >= 0.05) {
                        _injHint.classList.remove('hidden');
                    }
                }
                if (elPower) {
                    elPower.innerText = T('start_engine', 'START ENGINE');
                    elPower.className = "px-6 py-2 rounded font-bold bg-green-600 hover:bg-green-500 text-white shadow-lg shadow-green-900/50 transition-colors";
                }
            }

            if (isLoggingEnabled && result.valid) {
                let shouldLog = false;
                if (isDynoPullActive && dynoPhase === 'pull' && dynoMode !== 'rpm-lock') {
                    if (currentRpm >= lastLogRpm + 100 || datalogRows.length === 0) {
                        shouldLog = true;
                        lastLogRpm = Math.floor(currentRpm / 100) * 100;
                    }
                } else if ((isDynoPullActive && dynoPhase === 'pull' && dynoMode === 'rpm-lock')
                        || (appMode === 'drag' && dragPhase !== 'idle' && dragPhase !== 'finished')
                        || (appMode === 'garage' && isEngineRunning)) {
                    logTimeAccumulator += dt;
                    if (logTimeAccumulator >= 0.05 || datalogRows.length === 0) {
                        shouldLog = true;
                        logTimeAccumulator = 0;
                    }
                }

                if (shouldLog) {
                    let logTime = (appMode === 'dyno') ? dynoPullTimer : (appMode === 'garage' ? engineTimer : dragTime);
                    const logIat = tempUnitF ? Math.round(result.iat) : ((result.iat - 32) * 5 / 9).toFixed(1);
                    const _ect = currentCoolantC ?? 90;
                    const logEct = tempUnitF ? ((_ect * 9 / 5) + 32).toFixed(1) : _ect.toFixed(1);
                    let row = [
                        logTime.toFixed(3), Math.round(result.rpm), result.mapKpa.toFixed(1), (result.boostPsi || 0).toFixed(2),
                        actualTps.toFixed(1), logIat, result.commandedAfr.toFixed(2), result.actualAfr.toFixed(2),
                        result.timing.toFixed(1), result.ipw.toFixed(2), Math.round(result.vvtIntake), Math.round(result.vvtExhaust),
                        result.vvlActive ? 1 : 0, result.wgDuty.toFixed(1), result.knockCount, result.commandedVe.toFixed(1), Math.round(result.torque),
                        logEct, nitrousActive ? 1 : 0
                    ];

                    if (isDevLoggingEnabled) {
                        let optInt = 0, optExh = 0, maxTorque = -Infinity;
                        const vvtMode = physics.engine.vvt_parameters?.vvt_mode || "None";
                        const vvtDel = physics.getModModifiersAtRpm(currentRpm).vvt_delete;
                        let intAngles = (!vvtDel && (vvtMode === "Intake" || vvtMode === "Dual")) ? [0, 10, 20, 30, 40, 50] : [0];
                        let exhAngles = (!vvtDel && vvtMode === "Dual") ? [0, -10, -20, -30, -40, -50] : [0];
                        for(let i of intAngles) {
                            for(let e of exhAngles) {
                                let testState = physics.calculateEngineVVTState(currentRpm, i, e, result.vvlActive, vvtDel);
                                if (testState.torque > maxTorque) { maxTorque = testState.torque; optInt = i; optExh = e; }
                            }
                        }
                        row.push(
                            Math.round(result.turboSpeed), result.turboEff.toFixed(1), Math.round(result.cfm),
                            result.idealBaseTorque.toFixed(1), result.idealBaseVe.toFixed(1), result.idealBaseAfr.toFixed(2),
                            result.idealMbt.toFixed(1), result.timing.toFixed(1), result.actualTiming.toFixed(1),
                            result.modTorqueMult.toFixed(3), result.modVeMult.toFixed(3),
                            result.misfireChance.toFixed(1), Math.round(optInt), Math.round(Math.abs(optExh)),
                            (result.actualMapKpa ?? result.mapKpa).toFixed(1),  // lagged sensor MAP
                            result.mapKpa.toFixed(1),                           // effective MAP (with prediction)
                            (result.scExponent ?? 1).toFixed(3), (result.scChoke ?? 1).toFixed(3),
                            (result.totalStress ?? 0).toFixed(3), (result.knockChance ?? 0).toFixed(4)
                        );
                    }
                    datalogRows.push(row);
                }
            }
        }

        engineAngle += (currentRpm / 60) * 360 * dt;
        if (engineAngle >= 720) {
            engineAngle -= 720;
            if (physics.engineBlown && currentRpm > 50 && audio) audio.triggerMetalClack();
        }
    }

    // Tick down non-advanced grace period
    if (!advancedMode && simpleGracePeriod > 0) {
        simpleGracePeriod = Math.max(0, simpleGracePeriod - rawDt);
    }

    // --- KNOCK SENSING & CONTROL (post-loop, once per frame) ---
    if (isEngineRunning && !isCranking && result.valid) {
        const baseNoise = 0.05 + (currentRpm / 8000) * 0.25;
        const noiseVal  = result.knockTier === 'loud'
            ? 0.85 + (result.popIntensity || 0) * 0.15   // well above threshold — retard triggers
            : result.knockTier === 'normal'
            ? 0.50 + (result.popIntensity || 0) * 0.20   // above threshold — retard triggers
            : result.knockTier === 'quiet'
            ? 0.12 + (result.popIntensity || 0) * 0.08   // near threshold — may or may not trigger
            : result.knockTier === 'torque-only'
            ? baseNoise + Math.random() * 0.04            // faint blip, below threshold — ECU blind
            : baseNoise + Math.random() * 0.05;           // no knock event
        const noiseDb  = 20 * Math.log10(Math.max(noiseVal, 0.001));
        const threshDb = Interpolation.interpolate1D(currentRpm, ecu.knockThresholdAxis, ecu.knockThresholdCurve);

        // Frequency-domain spectrum — used by calibration waterfall and filtered detection
        const naturalFreqKHz = 573 / (sampleEngine.global_specs?.bore_mm ?? 86);
        const currentSpectrum = generateKnockSpectrum(result.knockTier, naturalFreqKHz, currentRpm);
        const filteredNoiseDb = computeKnockFilteredNoise(currentSpectrum, ecu.knockCenterFreq);

        // Update calibration waterfall (only when panel is visible)
        const calPanel = document.getElementById('knock-cal-panel');
        if (calPanel && !calPanel.classList.contains('hidden')) {
            knockWaterfallColumns.push(currentSpectrum);
            if (knockWaterfallColumns.length > 300) knockWaterfallColumns.shift();
            drawKnockFreqSpectrogram();
        }

        // knockDetected (single-channel) — used for overlay in all modes
        const knockDetected = result.popping && noiseDb > threshDb
            && !twoStepCut && !result.revLimit
            && actualTps >= ecu.knockMinTps
            && result.mapKpa >= ecu.knockMinMap;

        // knockDetectedFiltered — uses calibrated bandpass; gates retard.
        // No result.popping check: the sensor compares signal to threshold only.
        // If threshold is set below ambient noise, the sensor fires constantly (miscalibration).
        const knockDetectedFiltered = ecu.knockCalibrated && filteredNoiseDb > threshDb
            && !twoStepCut && !result.revLimit
            && actualTps >= ecu.knockMinTps
            && result.mapKpa >= ecu.knockMinMap;

        if (advancedMode && ecu.knockEnabled && ecu.knockCalibrated) {
            // Advanced Mode: run spectrogram and apply timing retard (calibrated path)
            if (!isSpectrogramPaused) {
                spectrogramData.push({ noise: filteredNoiseDb, threshold: threshDb });
                if (spectrogramData.length > 150) spectrogramData.shift();
                if (typeof updateSpectrogramChart === 'function') updateSpectrogramChart();
            }
            if (knockDetectedFiltered) {
                knockSensorCount++;
                knockRetardDeg = Math.min(ecu.knockMaxRetardDeg, knockRetardDeg + ecu.knockAttackDeg);
            } else if (knockRetardDeg > 0) {
                const revsPerFrame = (currentRpm / 60) * rawDt;
                knockRetardDeg = Math.max(0, knockRetardDeg - ecu.knockRecoveryDegPerRev * revsPerFrame);
            }
        } else if (advancedMode && ecu.knockEnabled && !ecu.knockCalibrated) {
            // Enabled but not calibrated: feed spectrogram with raw noise for visual feedback
            if (!isSpectrogramPaused) {
                spectrogramData.push({ noise: noiseDb, threshold: threshDb });
                if (spectrogramData.length > 150) spectrogramData.shift();
                if (typeof updateSpectrogramChart === 'function') updateSpectrogramChart();
            }
            knockRetardDeg = 0;
        } else {
            // Normal Mode (or knock control disabled): no software retard
            knockRetardDeg = 0;
        }

        // Knock overlay — fires in all modes regardless of advanced setting
        if (knockDetected) {
            knockOverlayCount++;
            knockOverlayTimer = 3.0;
            const overlayEl = document.getElementById('knock-overlay');
            const countEl   = document.getElementById('knock-overlay-count');
            if (overlayEl && overlayEl.dataset.overlayEnabled !== '0') {
                overlayEl.classList.remove('hidden');
                if (countEl) countEl.innerText = knockOverlayCount + ' event' + (knockOverlayCount !== 1 ? 's' : '');
            }
        }
    } else if (!isEngineRunning) {
        spectrogramData  = [];
        knockRetardDeg   = 0;
        knockSensorCount = 0;
        physics.knockCascadeNextTier = null;
        physics.knockCascadeFrames   = 0;
    }

    // Decay knock overlay timer
    if (knockOverlayTimer > 0) {
        knockOverlayTimer -= rawDt;
        if (knockOverlayTimer <= 0) {
            knockOverlayTimer = 0;
            const overlayEl = document.getElementById('knock-overlay');
            if (overlayEl) overlayEl.classList.add('hidden');
        }
    }

    // Starter audio edge detection — start/stop starter sound when cranking state changes
    if (isEngineRunning) {
        if (isCranking && !prevWasCranking) {
            if (audio) audio.startStarter();
        } else if (!isCranking && prevWasCranking) {
            if (audio) audio.stopStarter();
        }
    }
    prevWasCranking = isEngineRunning ? isCranking : false;

    // Fade post-start rich torque penalty over time (Advanced Mode only)
    if (advancedMode && postStartPenalty > 0 && postStartPenaltyDur > 0) {
        postStartPenaltyTimer += rawDt;
        const t = Math.min(1, postStartPenaltyTimer / postStartPenaltyDur);
        postStartPenalty = postStartPenaltyInit * (1 - t);
        if (t >= 1) postStartPenalty = 0;
    } else if (!advancedMode) {
        postStartPenalty = 0;
    }

    if (audio) audio.updateEngine(currentRpm, actualTps / 100, sampleEngine.global_specs.cylinders, result.boostPsi || 0, result.scRpm || 0, result.scMaxRpm || 1, result.scType || '');
    if (lastTpsForAudio - actualTps > 15 && (result.boostPsi || 0) > 2 && audio) audio.triggerBov(result.boostPsi);
    lastTpsForAudio = actualTps;
    if (result.popping && audio) {
        if (result.flamesMode) audio.triggerFlamethrower();
        else if (result.knockTier === 'loud')        audio.triggerPop(result.popIntensity);
        else if (result.knockTier === 'normal')      audio.triggerNormalKnock();
        else if (result.knockTier === 'quiet')       audio.triggerSoftKnock();
        else if (result.knockTier === 'torque-only') audio.triggerTorqueKnock();
        else audio.triggerPop(result.popIntensity);
    }
    if (audio) audio.setNitrous(nitrousActive && isEngineRunning);

    // Telemetry Updates
    const rpmEl = document.getElementById('val-rpm');
    if (rpmEl) rpmEl.innerText = Math.round(currentRpm);
    
    let _displayAfr = result.actualAfr || 14.7;
    if (advancedMode && ecu.o2DelayEnabled && isEngineRunning && afrHistory.length > 0) {
        const _nowDisp = performance.now();
        const _delayMs = currentRpm <= 1000 ? 300
            : currentRpm >= 3500 ? 0
            : 300 * (3500 - currentRpm) / 2500;
        const _targetT = _nowDisp - _delayMs;
        let _best = afrHistory[0];
        for (let _i = 1; _i < afrHistory.length; _i++) {
            if (afrHistory[_i].t <= _targetT) _best = afrHistory[_i];
            else break;
        }
        _displayAfr = _best.afr;
    }
    let afrDisplay = _displayAfr;
    if (ecu.displayLambda) afrDisplay = afrDisplay / 14.7;
    const afrEl = document.getElementById('val-afr');
    if (afrEl) afrEl.innerText = (isEngineRunning && !isFloodClear) ? afrDisplay.toFixed(ecu.displayLambda ? 3 : 1) : "--";
    const afrUnitEl = document.getElementById('lbl-afr-unit');
    if (afrUnitEl) afrUnitEl.innerText = ecu.displayLambda ? "LAMBDA" : "AFR";

    const stftRow = document.getElementById('row-stft');
    const stftEl  = document.getElementById('data-stft');
    if (stftRow) stftRow.classList.toggle('hidden', !ecu.closedLoopEnabled);
    if (stftEl && ecu.closedLoopEnabled) {
        const pct = (physics.stft * 100).toFixed(1);
        stftEl.innerText = isEngineRunning ? `${physics.stft >= 0 ? '+' : ''}${pct}%` : '--';
        stftEl.className = physics.stft > 0.05 ? 'text-red-400 font-bold'
                         : physics.stft < -0.05 ? 'text-blue-400 font-bold'
                         : 'text-green-400 font-bold';
    }

    const rpmGauge = document.getElementById('gauge-rpm');
    const gaugeMax = (sampleEngine?.mechanical_limits?.redline_rpm ?? 9000) * 1.1;
    if (rpmGauge) rpmGauge.style.strokeDasharray = `${Math.min(currentRpm / gaugeMax, 1) * 283} 283`;
    const afrVal = _displayAfr;
    const afrGauge = document.getElementById('gauge-afr');
    if (afrGauge) afrGauge.style.strokeDasharray = (isEngineRunning && !isFloodClear) ? `${Math.max(0, Math.min((16.0 - afrVal) / 6.0, 1)) * 283} 283` : "0 283";

    if (isEngineRunning && result.valid) {
        let statusText = "RUNNING";
        let statusColor = "text-green-400";
        if (appMode === 'drag' && dragPhase !== 'idle') { statusText = "DRAG RUN"; statusColor = "text-red-400"; }
        else if (isDynoPullActive) { statusText = "DYNO PULL"; statusColor = "text-purple-400"; }
        else if (isCranking) {
            if (floodLevel > 0 && isFloodClear) { statusText = "CLEARING FLOOD"; statusColor = "text-orange-400"; }
            else if (floodLevel > 0)             { statusText = "FLOODED";        statusColor = "text-red-400"; }
            else                                 { statusText = "CRANKING";       statusColor = "text-yellow-400"; }
        }
        else if (currentRpm <= liveIdleTargetRpm + ecu.idleRpmUpperLimit && actualTps <= liveIdleTps + 1) { statusText = "IDLING"; statusColor = "text-blue-400"; }

        if (advancedMode && (window.engineTriggerBaseOffset ?? 0) !== (ecu.triggerAngleOffset ?? 0)) {
            statusText = "NOT SYNCED"; statusColor = "text-yellow-400";
        }

        const statusEl = document.getElementById('data-status');
        if (statusEl) { statusEl.innerText = statusText; statusEl.className = `${statusColor} font-bold`; }

        const timingEl = document.getElementById('data-timing'); if (timingEl) timingEl.innerText = `${parseFloat(result.timing).toFixed(1)}°`;

        const knkRetardEl = document.getElementById('data-knk-retard');
        if (knkRetardEl) {
            knkRetardEl.innerText  = knockRetardDeg.toFixed(1) + '°';
            knkRetardEl.className  = knockRetardDeg > 0 ? 'text-orange-400 font-bold' : 'text-green-400 font-bold';
        }
        const knkCountEl = document.getElementById('data-knk-count');
        if (knkCountEl) {
            if (advancedMode) {
                if (ecu.knockEnabled && ecu.knockCalibrated) {
                    knkCountEl.innerText = knockSensorCount > 0 ? knockSensorCount : '--';
                    knkCountEl.className = knockSensorCount > 0 ? 'text-red-400 font-bold' : 'text-gray-500 font-bold';
                } else {
                    knkCountEl.innerText = '--';
                    knkCountEl.className = 'text-gray-500 font-bold';
                }
            } else {
                knkCountEl.innerText  = physics.knockCount;
                knkCountEl.className = physics.knockCount > 0 ? 'text-red-400 font-bold' : 'text-green-400 font-bold';
            }
        }
        // Update calibration timing adder countdown label
        const calCdEl = document.getElementById('cal-timing-countdown');
        if (calCdEl && calTimingAdderRemainSec > 0) {
            calCdEl.textContent = `+${calTimingAdderDeg}° — ${calTimingAdderRemainSec.toFixed(1)}s left`;
            calCdEl.classList.remove('hidden');
        }

        const ipwEl = document.getElementById('data-ipw'); if (ipwEl) ipwEl.innerText = `${parseFloat(result.ipw).toFixed(2)} ms`;
        const vvtInEl = document.getElementById('data-vvt-in');
        if (vvtInEl) vvtInEl.innerText = `${Math.round(result.vvtIntake)}° / ${Math.round(result.vvtIntakeTarget)}°`;
        const vvtExEl = document.getElementById('data-vvt-ex');
        if (vvtExEl) vvtExEl.innerText = `${Math.round(result.vvtExhaust)}° / ${Math.round(result.vvtExhaustTarget)}°`;
        
        if (ecu.fuelPressureModelEnabled) {
            const fpEl = document.getElementById('data-fuel-pressure');
            if (fpEl) fpEl.textContent = displayPressureKpa(ecu.effectiveFuelPressureKpa);
            const pdEl = document.getElementById('data-pump-demand');
            if (pdEl) {
                const pct = ecu.fuelPumpDemandPct;
                pdEl.textContent = pct.toFixed(0) + '%';
                pdEl.className = pct > 90 ? 'text-red-400 font-bold' : pct > 70 ? 'text-yellow-400 font-bold' : 'text-orange-300 font-bold';
            }
        }

        const boostEl = document.getElementById('data-boost');
        if (boostEl) {
            if (result.boostPsi !== undefined && result.boostPsi > 0.1) {
                boostEl.innerText = displayPressurePsi(result.boostPsi);
                boostEl.className = "text-orange-400 font-bold";
            } else {
                boostEl.innerText = displayPressurePsi(0);
                boostEl.className = "text-blue-400 font-bold";
            }
        }

        const pidInd = document.getElementById('ind-pid');
        if (pidInd) pidInd.className = (ecu.idleIgnEnabled && !isPedalHeldDown && !isDynoPullActive && appMode !== 'drag' && currentRpm <= liveIdleTargetRpm + ecu.idleRpmUpperLimit)
            ? "px-3 py-1 rounded bg-green-900/50 text-green-400 border border-green-500 shadow-[0_0_8px_rgba(16,185,129,0.5)] transition-colors"
            : "px-3 py-1 rounded bg-gray-700 text-gray-500 border border-transparent transition-colors";
            
        const overrunInd = document.getElementById('ind-overrun');
        if (overrunInd) overrunInd.className = result.overrun 
            ? "px-3 py-1 rounded bg-blue-900/50 text-blue-400 border border-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)] transition-colors"
            : "px-3 py-1 rounded bg-gray-700 text-gray-500 border border-transparent transition-colors";
            
        const vvlInd = document.getElementById('ind-vvl');
        if (vvlInd) vvlInd.className = result.vvlActive
            ? "px-3 py-1 rounded bg-purple-900/50 text-purple-400 border border-purple-500 shadow-[0_0_8px_rgba(168,85,247,0.5)] transition-colors"
            : "px-3 py-1 rounded bg-gray-700 text-gray-500 border border-transparent transition-colors";

        const nitrousStatusEl = document.getElementById('nitrous-status');
        if (nitrousStatusEl) nitrousStatusEl.classList.toggle('hidden', !nitrousActive);

        if (appMode === 'garage') {
            const btnLogGarage = document.getElementById('btn-log-garage');
            if (btnLogGarage) { btnLogGarage.title = ''; btnLogGarage.style.opacity = ''; btnLogGarage.style.cursor = ''; }
        }
    } else {
        if (appMode === 'garage') {
            const btnLogGarage = document.getElementById('btn-log-garage');
            if (btnLogGarage && !isLoggingEnabled) { btnLogGarage.title = 'Engine must be running to log!'; btnLogGarage.style.opacity = '0.45'; btnLogGarage.style.cursor = 'not-allowed'; }
        }
        ['ind-pid', 'ind-overrun', 'ind-vvl'].forEach(id => {
            const el = document.getElementById(id);
            if(el) el.className = "px-3 py-1 rounded bg-gray-700 text-gray-500 border border-transparent transition-colors";
        });
        const boostEl = document.getElementById('data-boost');
        if (boostEl) { boostEl.innerText = displayPressurePsi(0); boostEl.className = "text-blue-400 font-bold"; }
        const knkRetardElOff = document.getElementById('data-knk-retard');
        if (knkRetardElOff) { knkRetardElOff.innerText = '--'; knkRetardElOff.className = 'text-gray-500 font-bold'; }
        const knkCountElOff = document.getElementById('data-knk-count');
        if (knkCountElOff) { knkCountElOff.innerText = ecu.knockEnabled ? '--' : physics.knockCount; knkCountElOff.className = 'text-gray-500 font-bold'; }
        if (ecu.fuelPressureModelEnabled) {
            const fpElOff = document.getElementById('data-fuel-pressure');
            if (fpElOff) fpElOff.textContent = `-- ${useMetric ? 'kPa' : 'PSI'}`;
        }
    }

    // Shared UI updates (always run)
    const baroKpa = parseFloat(document.getElementById('sim-baro')?.value) || 101.3;
    const liveMapKpa = (isEngineRunning && result && result.valid && result.mapKpa !== undefined) ? result.mapKpa : baroKpa;
    
    const mapEl = document.getElementById('data-map'); if (mapEl) mapEl.innerText = displayPressureKpa(mapDisplayGauge ? liveMapKpa - baroKpa : liveMapKpa);
    const tpsEl = document.getElementById('data-tps'); if (tpsEl) tpsEl.innerText = `${actualTps.toFixed(1)}%`;

    document.getElementById('row-knk-retard')?.classList.toggle('hidden', !ecu.knockEnabled);
    document.getElementById('row-vvt-in')?.classList.toggle('hidden', !ecu.vvtPhaserEnabled);
    document.getElementById('row-vvt-ex')?.classList.toggle('hidden', !ecu.vvtPhaserEnabled);
    document.getElementById('row-eth')?.classList.toggle('hidden', !document.getElementById('inp-flex-tuning-en')?.checked);
    document.getElementById('row-fuel-pressure')?.classList.toggle('hidden', !ecu.fuelPressureModelEnabled);
    document.getElementById('row-pump-demand')?.classList.toggle('hidden', !ecu.fuelPressureModelEnabled);
    document.getElementById('row-twin-turbo')?.classList.toggle('hidden', !(typeof twinTurboEnabled !== 'undefined' && twinTurboEnabled) || !isEngineRunning);

    if (appMode === 'garage' && typeof updateTrace === 'function') {
        updateTrace(currentRpm, liveMapKpa, dTps);
    }

    // RPM Lock live HP/TQ display (once per frame, post-loop)
    if (isDynoPullActive && dynoPhase === 'pull' && dynoMode === 'rpm-lock' && isEngineRunning && result && result.valid) {
        const liveHp = (result.torque * dynoLockRpm) / 5252;
        const hpStat = document.getElementById('dyno-stat-hp');
        const tqStat = document.getElementById('dyno-stat-tq');
        if (hpStat) hpStat.innerText = `HP: ${liveHp.toFixed(1)}`;
        if (tqStat) tqStat.innerText = `TQ: ${result.torque.toFixed(1)} lb·ft`;
    }

    // Keep current IAT in °C for the IAT VE curve tracer
    if (result && result.iat != null) currentIatC = (result.iat - 32) * 5 / 9;

    // Coolant temperature simulation (once per frame, using rawDt for wall-clock accuracy)
    // Initialize on first frame or after a hard reset
    if (currentCoolantC === null) {
        currentCoolantC = getAmbientC();
        thermostatAngle = null;
    }
    if (!coolantSimEnabled) {
        currentCoolantC = isEngineRunning ? 90 : getAmbientC();
    } else if (!isEngineRunning) {
        // Engine off: cool toward ambient at 0.5°C/s
        const ambientC = getAmbientC();
        if (currentCoolantC > ambientC) {
            currentCoolantC = Math.max(ambientC, currentCoolantC - 0.5 * rawDt);
            thermostatAngle = null; // re-enter linear warmup on next start
        }
    } else {
        updateCoolantTemp(currentRpm, rawDt);
    }
    const ectEl = document.getElementById('data-ect');
    if (ectEl) ectEl.textContent = displayTemp(currentCoolantC);

    // Update voltage-dependent ECU properties from curves (uses batteryVoltage from previous frame — close enough)
    if (ecu) {
        ecu.batteryVoltage = batteryVoltage;
        ecu.injectorDeadTimeMs = Interpolation.interpolate1D(batteryVoltage, ecu.injDtAxis, ecu.injDtCurve);
        ecu.coilDwellMs = Interpolation.interpolate1D(batteryVoltage, ecu.coilDwellAxis, ecu.coilDwellCurve);
    }

    // Battery voltage simulation
    if (!isEngineRunning) {
        batteryVoltage = 12.5;
        altDuty = 80; altIntegral = 0; prevAltError = 0;
    } else if (isCranking) {
        if (engineTimer < 0.5) {
            const spikeFraction = Math.min(engineTimer / 0.1, 1.0);
            batteryVoltage = 12.5 - spikeFraction * 4.5;
        } else {
            batteryVoltage = 9.9 + 0.3 * Math.sin(2 * Math.PI * 4 * engineTimer);
        }
        altDuty = 80; altIntegral = 0; prevAltError = 0;
    } else if (ecu && advancedMode && ecu.smartAltEnabled) {
        // Smart alternator PID: alt pressure rises with RPM, PID backs off duty to hold target
        const altTargetV = Interpolation.interpolate2D(currentRpm, result.mapKpa || 100, ecu.altVoltRpmAxis, ecu.altVoltLoadAxis, ecu.altVoltTable);
        const altPressure = altTargetV + (currentRpm / 1000) * 2;
        const error = altTargetV - batteryVoltage;
        altIntegral += error * dt;
        const derivative = dt > 0 ? (error - prevAltError) / dt : 0;
        altDuty += ecu.smartAltP * error + ecu.smartAltI * altIntegral + ecu.smartAltD * derivative;
        altDuty = Math.max(0, Math.min(100, altDuty));
        prevAltError = error;
        // Drain curves smoothly from 0.05 V/s at resting voltage up to ~2 V/s well above it.
        // exp curve means spring-back is gentle close to 12.4 V and aggressive further above.
        const above = Math.max(0, batteryVoltage - 12.4);
        const drain = 0.05 + 1.95 * (1 - Math.exp(-above * 4));
        const chargeRate = (altPressure - batteryVoltage) * (altDuty / 100) * 2;
        batteryVoltage += (chargeRate - drain) * dt;
        batteryVoltage = Math.max(8.0, Math.min(16.0, batteryVoltage));
        if (batteryVoltage <= 8.0 && isEngineRunning) {
            isEngineRunning = false;
            if (audio) audio.stop();
            if (typeof abortDynoPull === 'function') abortDynoPull();
            if (appMode === 'drag' && typeof abortDragRun === 'function') abortDragRun();
            const errBox = document.getElementById('error-box');
            if (errBox) { errBox.classList.remove('hidden'); errBox.innerText = T('stalled_battery', 'STALLED: Battery voltage critically low (8 V).'); }
            const statusEl = document.getElementById('data-status');
            if (statusEl) { statusEl.innerText = T('stalled', 'STALLED'); statusEl.className = "text-red-400 font-bold"; }
        }
    } else {
        batteryVoltage = 14.5;
        altDuty = 80; altIntegral = 0; prevAltError = 0;
    }
    if (ecu) ecu.currentBatteryV = batteryVoltage;
    const battEl = document.getElementById('data-batt');
    if (battEl) {
        battEl.innerText = batteryVoltage.toFixed(1) + ' V';
        let battColor;
        if (batteryVoltage < 9.0)        battColor = 'text-red-500';
        else if (batteryVoltage < 11.5)  battColor = 'text-orange-400';
        else if (batteryVoltage < 12.0)  battColor = 'text-yellow-400';
        else if (batteryVoltage < 13.5)  battColor = 'text-lime-400';  // resting ~12.5 V
        else                             battColor = 'text-green-400'; // charging ~14.5 V
        battEl.className = battColor + ' font-bold';
    }

    // Smart alternator duty display (inside the tab, not the sidebar)
    const altDutyEl = document.getElementById('data-alt-duty');
    if (altDutyEl) {
        if (ecu && ecu.smartAltEnabled && isEngineRunning && !isCranking) {
            altDutyEl.innerText = Math.round(altDuty) + '%';
            altDutyEl.className = altDuty > 80 ? 'text-orange-400 font-bold' : 'text-green-400 font-bold';
        } else {
            altDutyEl.innerText = ecu && ecu.smartAltEnabled ? '--' : 'N/A';
            altDutyEl.className = 'text-gray-500 font-bold';
        }
    }

    // Coil melt detection (once per animation frame, using wall-clock rawDt)
    if (isEngineRunning && !isCranking && result && result.valid) {
        const MELT_DWELL_THRESHOLD = 8.0; // ms
        const MELT_CHANCE_PER_SEC  = 0.50;
        if (ecu.coilDwellMs > MELT_DWELL_THRESHOLD) {
            coilMeltAccumulator += rawDt;
            while (coilMeltAccumulator >= 1.0) {
                coilMeltAccumulator -= 1.0;
                if (Math.random() < MELT_CHANCE_PER_SEC) triggerCoilMelt();
            }
        } else {
            coilMeltAccumulator = 0;
        }
    }

    // Block Visualizer
    const cylCount     = sampleEngine.global_specs.cylinders;
    const layout       = sampleEngine.global_specs.engine_layout;
    // Rotary: each rotor produces 3 pulses per 720° crank cycle, so the effective
    // firing interval is 720 / (rotors × 3). Piston engines: 720 / cylinders.
    const fireInterval = layout === 'Rotary' ? (720 / (cylCount * 3)) : (720 / cylCount);
    const activeIndex  = Math.floor(engineAngle / fireInterval) % (layout === 'Rotary' ? cylCount * 3 : firingOrderArr.length);
    // For rotary, map pulse index to rotor number (rotors fire in round-robin); for piston, look up firing order array.
    const activeCyl    = layout === 'Rotary' ? ((activeIndex % cylCount) + 1) : firingOrderArr[activeIndex];
    const disabledCyls = ecu.disabledCylinders;

    // Determine which cylinders should flash this frame
    const firingCyls = new Set();
    if (activeCyl && isEngineRunning && result && !result.overrun && !result.sparkCut && !disabledCyls.has(activeCyl)) {
        firingCyls.add(activeCyl);
        // Wasted spark: light up the paired cylinder simultaneously
        if (ecu.ignitionMode === "wasted_spark") {
            const pairs = ecu.getCoilPairs(firingOrderArr, cylCount);
            for (const pair of pairs) {
                if (pair.includes(activeCyl)) {
                    pair.forEach(c => { if (!disabledCyls.has(c)) firingCyls.add(c); });
                    break;
                }
            }
        }
    }

    document.querySelectorAll('#engine-block-viz div').forEach(el => {
        const cylNum = parseInt(el.id.replace('cyl-', ''));
        if (disabledCyls.has(cylNum)) return; // updateMeltedCylinderDisplay owns melted state
        if (firingCyls.has(cylNum)) {
            if (layout === "Rotary") el.classList.replace('border-b-gray-800', 'border-b-red-500');
            else {
                el.classList.replace('bg-gray-800', 'bg-red-500');
                el.classList.replace('border-gray-600', 'border-red-400');
                el.classList.replace('text-gray-500', 'text-white');
                el.style.boxShadow = '0 0 8px rgba(239, 68, 68, 0.8)';
            }
        } else {
            if (layout === "Rotary") el.classList.replace('border-b-red-500', 'border-b-gray-800');
            else {
                el.classList.replace('bg-red-500', 'bg-gray-800');
                el.classList.replace('border-red-400', 'border-gray-600');
                el.classList.replace('text-white', 'text-gray-500');
                el.style.boxShadow = 'none';
            }
        }
    });

    // Distributor cap rotor animation
    if (ecu.ignitionMode === "distributor") {
        const rotorEl = document.getElementById('dist-rotor');
        const wrap    = document.getElementById('distributor-cap-wrap');
        if (wrap) wrap.classList.remove('hidden');
        if (rotorEl && sampleEngine) {
            const cyls     = sampleEngine.global_specs.cylinders;
            const R        = 42;
            // Distributor runs at cam speed = half crank speed; one full rotation per 720° crank
            const rotorDeg = (engineAngle / 720) * 360 - 90; // -90 so 0° starts at top
            const rad      = rotorDeg * Math.PI / 180;
            rotorEl.setAttribute('x2', String((R) * Math.cos(rad)));
            rotorEl.setAttribute('y2', String((R) * Math.sin(rad)));

            // Highlight the terminal the rotor is currently pointing to
            const termIdx = Math.floor(((engineAngle / 720) * cyls) % cyls);
            for (let i = 0; i < cyls; i++) {
                const dot = document.getElementById(`dist-term-${i}`);
                const lbl = dot ? dot.nextElementSibling : null;
                if (!dot) continue;
                const isActive  = i === termIdx && isEngineRunning && result && !result.sparkCut && !result.overrun;
                const isMelted  = ecu.disabledCylinders.has(firingOrderArr[i]);
                if (isMelted) {
                    dot.setAttribute('fill', '#7f1d1d');
                    dot.setAttribute('stroke', '#991b1b');
                    if (lbl) lbl.setAttribute('fill', '#f87171');
                } else if (isActive) {
                    dot.setAttribute('fill', '#ef4444');
                    dot.setAttribute('stroke', '#fca5a5');
                    if (lbl) lbl.setAttribute('fill', '#ffffff');
                } else {
                    dot.setAttribute('fill', '#374151');
                    dot.setAttribute('stroke', '#6b7280');
                    if (lbl) lbl.setAttribute('fill', '#9ca3af');
                }
            }
        }
    } else {
        const wrap = document.getElementById('distributor-cap-wrap');
        if (wrap) wrap.classList.add('hidden');
    }

    if (appMode === 'drag') {
        const mphEl = document.getElementById('drag-hud-mph'); if (mphEl) mphEl.innerText = (useMetric ? dragVel * 3.6 : dragVel * 2.237).toFixed(1);
        const speedUnitEl = document.getElementById('drag-hud-speed-unit'); if (speedUnitEl) speedUnitEl.textContent = useMetric ? 'KPH' : 'MPH';
        const gearEl = document.getElementById('drag-hud-gear'); if (gearEl) gearEl.innerText = dragGear + 1;
        const gEl = document.getElementById('drag-hud-g'); if (gEl) gEl.innerText = dragGForce.toFixed(2);
        const timeEl = document.getElementById('drag-hud-time'); if (timeEl) timeEl.innerText = dragTime.toFixed(3);
        let progress = Math.min(100, (dragDist / 402.33) * 100);
        const carEl = document.getElementById('drag-car'); if (carEl) carEl.style.left = `calc(${progress}% - 24px)`;
    }

    requestAnimationFrame(physicsLoop);
}

// Global Key Listeners for Pedal and Shifting
document.addEventListener('keydown', (e) => {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

    if (e.code === 'Space') {
        e.preventDefault();
        if (dragManualMode && appMode === 'drag' && dragPhase !== 'idle' && dragPhase !== 'finished') {
            dragThrottlePressed = true;
        } else {
            if (!isPedalHeldDown) pressPedal();
        }
    }

    if (e.key.toLowerCase() === 'c' && dragManualMode && appMode === 'drag') {
        if (!dragClutchPressed) {
            dragClutchPressed = true;
            dragManualSlipTimer = 0;
        }
    }

    if (e.key.toLowerCase() === 's') {
        if (dragManualMode && appMode === 'drag' && dragPhase === 'pull') {
            if (dragClutchPressed) {
                const validGears = currentTransmission.gears.filter(g => g > 0);
                if (dragGear < validGears.length - 1) {
                    dragGear++;
                    dragManualSlipTimer = 0;
                }
            }
        } else {
            if (typeof manualShift === 'function') manualShift();
        }
    }

    if (e.key.toLowerCase() === 'x' && dragManualMode && appMode === 'drag'
            && (dragPhase === 'pull' || dragPhase === 'finished')) {
        if (dragClutchPressed) {
            dragGear = Math.max(0, dragGear - 1);
            dragManualSlipTimer = 0;
        }
    }

    if (e.key.toLowerCase() === 'n') nitrousSwitchHeld = true;
});

document.addEventListener('keyup', (e) => {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

    if (e.code === 'Space') {
        e.preventDefault();
        if (dragManualMode && appMode === 'drag' && dragPhase !== 'idle' && dragPhase !== 'finished') {
            dragThrottlePressed = false;
        } else {
            releasePedal();
        }
    }

    if (e.key.toLowerCase() === 'c' && dragManualMode && appMode === 'drag') {
        dragClutchPressed = false;
        dragManualSlipTimer = 0;
        // Releasing clutch on the line launches the car
        if (dragPhase === 'waitForLaunch' && typeof launchCar === 'function') launchCar();
    }

    if (e.key.toLowerCase() === 'n') nitrousSwitchHeld = false;
});

// Initialization
document.addEventListener('DOMContentLoaded', () => {
    if (typeof renderAllTables === 'function') renderAllTables();
    if (typeof initUnitLabels === 'function') initUnitLabels(); else if (typeof updateWizardPreviews === 'function') updateWizardPreviews();
    if (typeof applyAdvancedMode === 'function') applyAdvancedMode(false);
    if (typeof switchTab === 'function') switchTab('tab-ve', document.querySelector('.tab-dropdown-btn'));
    if (typeof updateVVTMenuVisibility === 'function') updateVVTMenuVisibility();
    if (typeof initDragListeners === 'function') initDragListeners();

    const btnPauseSpectrogram = document.getElementById('btn-pause-spectrogram');
    if (btnPauseSpectrogram) {
        btnPauseSpectrogram.addEventListener('click', () => {
            isSpectrogramPaused = !isSpectrogramPaused;
            btnPauseSpectrogram.innerText = isSpectrogramPaused ? 'PLAY' : 'PAUSE';
            btnPauseSpectrogram.classList.toggle('bg-blue-600', isSpectrogramPaused);
            btnPauseSpectrogram.classList.toggle('bg-gray-700', !isSpectrogramPaused);
            btnPauseSpectrogram.classList.toggle('hover:bg-blue-500', isSpectrogramPaused);
            btnPauseSpectrogram.classList.toggle('hover:bg-gray-600', !isSpectrogramPaused);
        });
    }

    // --- Knock Frequency Calibration Mode ---
    const btnEnterCal = document.getElementById('btn-enter-calibration');
    if (btnEnterCal) {
        btnEnterCal.addEventListener('click', () => {
            const panel = document.getElementById('knock-cal-panel');
            if (!panel) return;
            panel.classList.remove('hidden');
            btnEnterCal.classList.add('hidden');
            // Size the canvas now that it is visible
            const canvas = document.getElementById('knock-freq-spectrogram');
            if (canvas && canvas.offsetWidth > 0) {
                canvas.width  = canvas.offsetWidth;
                canvas.height = canvas.offsetHeight;
                knockFreqCanvasCtx = canvas.getContext('2d');
            } else if (canvas) {
                canvas.width  = 600;
                canvas.height = 180;
                knockFreqCanvasCtx = canvas.getContext('2d');
            }
            knockWaterfallColumns = [];
            // Log expected knock resonance frequency to console
            const boreMm = sampleEngine.global_specs?.bore_mm ?? 86;
            const idealFreqKHz = 573 / boreMm;
            console.log(`[Knock Cal] Engine bore: ${boreMm} mm | Expected knock resonance: ${idealFreqKHz.toFixed(2)} kHz  (formula: 573 ÷ bore_mm)`);
            // Sync slider to current ecu value
            const rangeEl = document.getElementById('inp-knk-center-freq-range');
            const numEl   = document.getElementById('inp-knk-center-freq');
            if (rangeEl) rangeEl.value = ecu.knockCenterFreq;
            if (numEl)   numEl.value   = ecu.knockCenterFreq;
        });
    }

    const btnConfirmCal = document.getElementById('btn-confirm-calibration');
    if (btnConfirmCal) {
        btnConfirmCal.addEventListener('click', () => {
            const numEl = document.getElementById('inp-knk-center-freq');
            ecu.knockCenterFreq = parseFloat(numEl?.value) || 7.0;
            ecu.knockCalibrated = true;
            // Reset any active timing adder
            calTimingAdderDeg       = 0;
            calTimingAdderRemainSec = 0;
            // Close panel, clear buffer
            const panel = document.getElementById('knock-cal-panel');
            if (panel) panel.classList.add('hidden');
            knockWaterfallColumns = [];
            knockFreqCanvasCtx    = null;
            // Show status badge
            const status = document.getElementById('knock-cal-status');
            if (status) status.classList.remove('hidden');
            const freqDisplay = document.getElementById('knk-cal-freq-display');
            if (freqDisplay) freqDisplay.textContent = ecu.knockCenterFreq.toFixed(1);
            // Re-show enter button (now says recalibrate via separate btn)
            const enterBtn = document.getElementById('btn-enter-calibration');
            if (enterBtn) enterBtn.classList.add('hidden'); // hidden; recalibrate btn handles re-entry
            // Unlock Enable Knock Control checkbox
            if (typeof syncKnockCalibrationState === 'function') syncKnockCalibrationState();
        });
    }

    const btnRecalibrate = document.getElementById('btn-recalibrate');
    if (btnRecalibrate) {
        btnRecalibrate.addEventListener('click', () => {
            // Hide status, show enter button, reset calibration
            const status = document.getElementById('knock-cal-status');
            if (status) status.classList.add('hidden');
            const enterBtn = document.getElementById('btn-enter-calibration');
            if (enterBtn) enterBtn.classList.remove('hidden');
            ecu.knockCalibrated     = false;
            ecu.knockEnabled        = false;
            calTimingAdderDeg       = 0;
            calTimingAdderRemainSec = 0;
            const checkbox = document.getElementById('inp-knk-enabled');
            if (checkbox) checkbox.checked = false;
            const params = document.getElementById('knock-control-params');
            if (params) params.classList.add('hidden');
            if (typeof syncKnockCalibrationState === 'function') syncKnockCalibrationState();
        });
    }

    // Calibration timing adder Apply button
    const btnCalTimingApply = document.getElementById('btn-cal-timing-apply');
    if (btnCalTimingApply) {
        btnCalTimingApply.addEventListener('click', () => {
            const deg = parseFloat(document.getElementById('inp-cal-timing-adder')?.value) || 5;
            const dur = parseFloat(document.getElementById('inp-cal-timing-dur')?.value) || 5;
            calTimingAdderDeg       = Math.max(0, deg);
            calTimingAdderRemainSec = Math.max(0.5, dur);
            const cdEl = document.getElementById('cal-timing-countdown');
            if (cdEl) {
                cdEl.textContent = `+${calTimingAdderDeg}° — ${calTimingAdderRemainSec.toFixed(1)}s left`;
                cdEl.classList.remove('hidden');
            }
        });
    }

    // Slider ↔ number input sync
    const calRange = document.getElementById('inp-knk-center-freq-range');
    const calNum   = document.getElementById('inp-knk-center-freq');
    if (calRange && calNum) {
        calRange.addEventListener('input', () => { calNum.value = calRange.value; ecu.knockCenterFreq = parseFloat(calRange.value) || 7.0; });
        calNum.addEventListener('input',   () => { calRange.value = calNum.value; ecu.knockCenterFreq = parseFloat(calNum.value)  || 7.0; });
    }

    // Enable Knock Control checkbox → show/hide sub-params
    const knkCheckbox = document.getElementById('inp-knk-enabled');
    if (knkCheckbox) {
        knkCheckbox.addEventListener('change', function () {
            const params = document.getElementById('knock-control-params');
            if (params) params.classList.toggle('hidden', !this.checked);
        });
    }

    // N2O switch button listeners (switch/button activation mode)
    document.querySelectorAll('.btn-nitrous-switch').forEach(btn => {
        btn.addEventListener('mousedown', () => { nitrousSwitchHeld = true; });
        btn.addEventListener('touchstart', () => { nitrousSwitchHeld = true; }, { passive: true });
        btn.addEventListener('mouseup', () => { nitrousSwitchHeld = false; });
        btn.addEventListener('mouseleave', () => { nitrousSwitchHeld = false; });
        btn.addEventListener('touchend', () => { nitrousSwitchHeld = false; });
    });
    // Load the hardcoded default engine (D16A6) immediately so the selector,
    // transmission, and all UI fields are correct before any network fetch.
    if (typeof loadEngine === 'function') loadEngine(sampleEngine);

    if (typeof renderUpgradesUI === 'function') renderUpgradesUI();
    if (typeof initDynoCharts === 'function') initDynoCharts();
    if (typeof initSpectrogramChart === 'function') initSpectrogramChart();

    requestAnimationFrame(physicsLoop);

    // Warn before navigating away; show export reminder if user stays
    window.addEventListener('beforeunload', (e) => {
        e.preventDefault();
        e.returnValue = '';
        // Show export reminder after the browser dialog is dismissed (user chose to stay)
        setTimeout(() => {
            const modal = document.getElementById('modal-export-reminder');
            if (!modal) return;
            modal.classList.remove('hidden');  // Tailwind (TuningSim.html)
            modal.style.display = 'flex';      // inline-styled version (Shopify)
        }, 0);
    });
});

// ---------------------------------------------------------------------------
// Knock Frequency Calibration — helper functions
// ---------------------------------------------------------------------------

/**
 * Generate a 128-bin power spectral density (dB) for the current frame.
 * Background: RPM-scaled mechanical noise at low freqs, white noise floor at high freqs.
 * Knock event: Gaussian peak at naturalFreqKHz with a realistic spread, plus a harmonic.
 */
function generateKnockSpectrum(knockTier, naturalFreqKHz, rpm) {
    const bins    = KNOCK_FREQ_BINS;
    const maxKHz  = KNOCK_FREQ_MAX;
    const rpmNorm = Math.min(rpm / 8000, 1.0);
    const spectrum = new Float32Array(bins);

    for (let i = 0; i < bins; i++) {
        const f = (i / (bins - 1)) * maxKHz;
        // Mechanical noise: dominant at 0–4 kHz, rolls off sharply above
        const mech = -10 - f * 1.0 + rpmNorm * 7 + (Math.random() - 0.5) * 1.5;
        // Electronic noise floor: roughly flat
        const floor = -29 + (Math.random() - 0.5) * 2.5;
        // Cap background so it never reads as knock
        spectrum[i] = Math.min(Math.max(mech, floor), -6);
    }

    // Knock spike — broad Gaussian hump with a harmonic
    if (knockTier === 'loud' || knockTier === 'normal' || knockTier === 'quiet') {
        // Peak dB at the centre of the fundamental
        const peakDb  = knockTier === 'loud' ? -1.5 : knockTier === 'normal' ? -7 : -10;
        const sigma   = 1.1;  // kHz — controls the width of the hump

        // Fundamental: interpolate between background and peak using Gaussian weight
        for (let i = 0; i < bins; i++) {
            const f          = (i / (bins - 1)) * maxKHz;
            const gaussFactor = Math.exp(-0.5 * ((f - naturalFreqKHz) / sigma) ** 2);
            // At centre: value = peakDb.  At ±sigma: value = ~37 % of the way from bg to peak.
            const spikeDb = spectrum[i] + (peakDb - spectrum[i]) * gaussFactor;
            if (spikeDb > spectrum[i]) spectrum[i] = spikeDb;
        }

        // First harmonic at 2× — 7 dB weaker, narrower
        const harmonicFreq = naturalFreqKHz * 2;
        const harmonicPeak = peakDb - 7;
        const harmonicSigma = 0.7;
        if (harmonicFreq < maxKHz) {
            for (let i = 0; i < bins; i++) {
                const f          = (i / (bins - 1)) * maxKHz;
                const gaussFactor = Math.exp(-0.5 * ((f - harmonicFreq) / harmonicSigma) ** 2);
                const spikeDb     = spectrum[i] + (harmonicPeak - spectrum[i]) * gaussFactor;
                if (spikeDb > spectrum[i]) spectrum[i] = spikeDb;
            }
        }
    }

    return spectrum;
}

/**
 * Peak dB within ±bandwidth of centerFreqKHz.
 * Returns noise-floor level when center frequency misses the knock peak.
 */
function computeKnockFilteredNoise(spectrum, centerFreqKHz) {
    const bins      = KNOCK_FREQ_BINS;
    const maxKHz    = KNOCK_FREQ_MAX;
    const bandwidth = 0.75; // kHz
    let peak = -60;
    for (let i = 0; i < bins; i++) {
        const f = (i / (bins - 1)) * maxKHz;
        if (Math.abs(f - centerFreqKHz) <= bandwidth) {
            if (spectrum[i] > peak) peak = spectrum[i];
        }
    }
    return peak;
}

/**
 * Render the waterfall canvas from knockWaterfallColumns.
 * Each column = one frame, X = time (left = oldest, right = newest).
 * Y = frequency (bottom = 0 kHz, top = 20 kHz).
 * Color = viridis-like dB heatmap: black → blue → cyan → green → yellow → red.
 * Draws a dashed yellow line at ecu.knockCenterFreq.
 */
function drawKnockFreqSpectrogram() {
    const ctx  = knockFreqCanvasCtx;
    if (!ctx) return;
    const W    = ctx.canvas.width;
    const H    = ctx.canvas.height;
    const bins = KNOCK_FREQ_BINS;
    const cols = knockWaterfallColumns.length;

    const imgData = ctx.createImageData(W, H);
    const px      = imgData.data;

    for (let cx = 0; cx < W; cx++) {
        if (cols === 0) {
            // Fill black
            for (let cy = 0; cy < H; cy++) {
                const idx = (cy * W + cx) * 4;
                px[idx] = px[idx + 1] = px[idx + 2] = 0; px[idx + 3] = 255;
            }
            continue;
        }
        // Map canvas X to column index (right edge = newest)
        const colIdx = Math.min(Math.floor((cx / W) * cols), cols - 1);
        const col    = knockWaterfallColumns[colIdx];

        for (let cy = 0; cy < H; cy++) {
            // Top of canvas = high frequency
            const binIdx = Math.min(Math.floor(((H - 1 - cy) / H) * bins), bins - 1);
            const [r, g, b] = knockDbToColor(col[binIdx]);
            const idx = (cy * W + cx) * 4;
            px[idx] = r; px[idx + 1] = g; px[idx + 2] = b; px[idx + 3] = 255;
        }
    }

    ctx.putImageData(imgData, 0, 0);

    // Draw center frequency marker — dashed yellow
    const maxKHz   = KNOCK_FREQ_MAX;
    const markerY  = H - ((ecu.knockCenterFreq - 0) / maxKHz) * H;
    ctx.save();
    ctx.strokeStyle = '#FBBF24';
    ctx.lineWidth   = 1.5;
    ctx.setLineDash([5, 4]);
    ctx.beginPath();
    ctx.moveTo(0, markerY);
    ctx.lineTo(W, markerY);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();

}

/**
 * Map a dB value (range -20 to -1) to an RGB colour using a viridis-like heatmap.
 * -20 dB → black, -14 → blue, -9 → cyan/green, -5 → yellow, -1 → red-white.
 */
function knockDbToColor(db) {
    // Normalise to [0, 1]
    const t = Math.max(0, Math.min(1, (db + 20) / 19));
    let r, g, b;
    if (t < 0.20) {
        const u = t / 0.20;
        r = Math.round(u * 5);    g = 0;                      b = Math.round(80 + u * 120);
    } else if (t < 0.40) {
        const u = (t - 0.20) / 0.20;
        r = 0;                    g = Math.round(u * 180);    b = Math.round(200 - u * 60);
    } else if (t < 0.60) {
        const u = (t - 0.40) / 0.20;
        r = 0;                    g = Math.round(180 + u * 40); b = Math.round(140 - u * 140);
    } else if (t < 0.80) {
        const u = (t - 0.60) / 0.20;
        r = Math.round(u * 230);  g = 220;                    b = 0;
    } else {
        const u = (t - 0.80) / 0.20;
        r = 255;                  g = Math.round(220 - u * 180); b = Math.round(u * 40);
    }
    return [r, g, b];
}

function getCurrentGear() {
    if (typeof appMode !== 'undefined') {
        if (appMode === 'drag') {
            return (typeof dragGear !== 'undefined') ? dragGear + 1 : 1;
        } else if (appMode === 'dyno') {
            return (typeof dynoGear !== 'undefined') ? dynoGear : 4;
        }
    }
    return 1; // Default to 1st gear in garage / stand mode
}
